/**
 * game-editors/ff6.js -- Final Fantasy VI (US, cart title "FINAL FANTASY 3")
 * party stat/equipment/status editor. See game-editors/index.js for the
 * plugin contract this implements and why it edits live WRAM rather than an
 * offline save file.
 *
 * Address provenance: WRAM $1600-$184F is the game's "active party" table --
 * 16 fixed-size records covering both the current field/status-menu party
 * and whoever's actually fighting in battle (a scripted battle can also
 * populate a slot with a non-party guest, e.g. an escorting soldier, which
 * is why read() below filters to known playable character IDs). The table
 * base, 37-byte record stride, and the level/HP/MP field offsets match
 * long-standing FF6 ROM-hacking documentation (see the RAM map at
 * datacrystal.tcrf.net/wiki/Final_Fantasy_VI/RAM_map) and were independently
 * confirmed in this session by booting a real cartridge dump under this
 * exact core, mashing through the intro to the mandatory Narshe battle, and
 * checking the live values against what should be true at that exact,
 * deterministic point in the game: exactly three populated slots (Terra
 * plus her two Imperial-soldier escorts), character ID 0 at Terra's slot,
 * MP of 0 for the non-magic soldiers but not for Terra, and HP == max HP
 * before any damage had landed. The table's own start/end ($1600-$184F,
 * 16*37 bytes) and the level/HP/MP offsets landed on exactly the addresses
 * the RAM map cites, byte for byte. The four secondary stat offsets
 * (Vigor/Speed/Stamina/Magic Power) are corroborated the same way plus a
 * thematic check -- the two physical-fighter soldiers came out with higher
 * Vigor and lower Magic Power than Terra, a mage, which is exactly backwards
 * if the offsets were wrong.
 *
 * The DEAD flag, equipment slot offsets, and item ID -> name table all rest
 * on stronger ground than the stat offsets above: all three were
 * cross-checked directly against the actual FF6 disassembly source
 * (github.com/everything8215/ff6 -- src/common/const.inc's STATUS1/ITEM
 * enums, src/menu/field_menu.asm's and src/menu/equip.asm's field accesses
 * against these exact byte offsets, and assets/text/item_name.en.txt for the
 * item names themselves, in enum order), not just a wiki paraphrase or an
 * in-session inference.
 *
 * If a future core rebuild or a different ROM revision ever makes any of
 * this look wrong (garbage values, a name that doesn't match who's actually
 * in the party), re-verify the same way -- boot a real cart, reach a
 * deterministic known state, cross-check -- before trusting it again.
 *
 * Spells and Lore live in two entirely separate, GLOBAL (not per-active-slot)
 * tables -- $1A6E (per-character-ID magic-learned bytes) and $1D29 (a party-
 * wide Lore bitmask) -- rather than anywhere inside the $1600 record above,
 * since spell/Lore knowledge is permanent whether or not that character is
 * currently in the party. Both were confirmed the same way, directly against
 * `src/menu/skills.asm`'s `GetMagicLearnPct` and `ExpandLoreList`. Gau's Rage
 * list (256 possible monsters, a 32-byte mask at $1D2C) was deliberately left
 * out -- editing 256 individual toggles isn't a reasonable UI, unlike the 54
 * spells or 24 Lores.
 *
 * Esper bonds are back to being a plain byte in the $1600 record (offset
 * $1e, right before the weapon slot) -- unlike spells/Lore, which Esper a
 * character is bonded to right now IS per-active-slot state, not permanent
 * account-wide data, so it lives exactly where equipment does and uses the
 * same 0xFF "none" sentinel. Confirmed against
 * `src/menu/field_menu.asm`'s `DrawCharGenjuName`.
 *
 * The character's editable display NAME is also back in the $1600 record
 * (offset $2, 6 bytes, `DrawCharName`'s `lda $0002,y` loop) -- it's a
 * per-slot field like equipment, not permanent roster data, since the
 * whole point of it is that the player can change it at any time via the
 * in-game Rename item.
 *
 * Two more GLOBAL tables, same neighbourhood as the spell-learned table:
 * owned Espers (not to be confused with which Esper a character is bonded
 * to) are a 4-byte/32-bit mask at $1A69, immediately before the $1A6E
 * spell table with no gap -- confirmed against `GetGenjuList`. The general
 * item inventory is two parallel 256-byte arrays, item IDs at $1869 and
 * quantities at $1969 -- confirmed against `CountInventoryItems`'s
 * `cpx #$0100` slot count and `cmp #ITEM::EMPTY` sentinel check.
 *
 * Key items ("rare items" in the disassembly's own naming) are a 28-bit
 * global mask at $1EBA-$1EBD, same shape as Lore/Esper ownership -- see
 * KEY_ITEM_BASE below. Only 20 of the 28 bits have a name in the game's own
 * text table; the other 8 are left untouched rather than guessed at.
 *
 * Treasure chests are deliberately NOT covered by this plugin. "Has this
 * chest been opened" is one bit out of a single, huge, general-purpose
 * event-bit array (around $1E40, up to ~512 bits) shared with hundreds of
 * unrelated story/NPC/dialogue flags -- a chest's specific bit number only
 * exists inside a `TreasureProp` table keyed by map + on-screen position,
 * not a clean per-chest name list, and that raw table isn't even present as
 * readable data in the disassembly checkout used for this research (only
 * the code that reads it). Exposing raw event-bit numbers with no location
 * name would be both useless and risky -- the same array holds bits that
 * gate story progression, so guessing wrong could break a save in a much
 * less obvious way than a bad stat edit. Skipped for the same reason Gau's
 * 256-monster Rage list was: no reasonable, safe UI for it right now.
 *
 * The Item Editor also covers Hit Rate/Magic Defense (offset 21, one more
 * dual-meaning byte exactly like `power`/offset 20 -- `hit_rate` and
 * `mag_def` are literally the same macro variable in item_prop.mac), Evade/
 * Magic Block (offset 26, packed nibbles -- but a TABLE INDEX, not a plain
 * scaled value; see the block comment above `EQUIP_EVADE_TABLE`, added
 * after a user's own maxed-out edit read back as garbage in game), and
 * Weapon Special Effect (offset 27's high nibble, one of 16 hardcoded named
 * battle behaviours like Atma Weapon's "ignore Defense" -- see the block
 * comment above `WEAPON_SPECIAL_NAMES`). All defaults were parsed from
 * item_prop.asm and verified against this exact ROM for all 231 equippable
 * items; the one real mismatch that turned up while verifying Hit Rate/Magic
 * Defense (item 174, Inviz Edge) revealed that Shadow's five throwable
 * "Edge" items (171-175) are CONSUMABLE-typed despite sitting in the
 * weapon/tool id range, so offset 21 (and offset 27's nibble split) means
 * something else entirely for those five -- see `EDGE_CONSUMABLE_ITEM_IDS`.
 */

const TABLE_BASE = 0x1600;
const RECORD_SIZE = 37;
const SLOT_COUNT = 16;

const OFFSET = {
  charId: 0x00,
  name: 0x02,
  level: 0x08,
  hp: 0x09,
  maxHp: 0x0b,
  mp: 0x0d,
  maxMp: 0x0f,
  status1: 0x14,
  vigor: 0x1a,
  speed: 0x1b,
  stamina: 0x1c,
  magicPower: 0x1d,
  esper: 0x1e,
  weapon: 0x1f,
  shield: 0x20,
  helmet: 0x21,
  armor: 0x22,
  relic1: 0x23,
  relic2: 0x24,
};

/** Which OFFSET keys are 16-bit (little-endian); everything else is one byte. */
const WORD_FIELDS = new Set(['hp', 'maxHp', 'mp', 'maxMp']);

/**
 * "Status 1": the one status byte this persistent table actually keeps (per
 * the disassembly's own comment, "harmful status that persist after
 * battle" -- transient battle-only ailments like Sleep/Confuse/Regen live
 * only in a separate battle-working-memory struct this plugin never touches).
 * Confirmed against src/common/const.inc's STATUS1 enum and multiple call
 * sites reading/writing this exact $0014,x offset (src/menu/field_menu.asm,
 * src/menu/status.asm, src/menu/item.asm).
 */
const STATUS1_DEAD = 0x80;
const STATUS1_ZOMBIE = 0x02;

/** The subset of STATUS1 exposed as curable "ailments" in the UI -- excludes
 * DEAD (handled by its own Revive button above) and MAGITEK (a plot-device
 * flag for Terra's intro sequence, not a player-facing ailment). */
const AILMENTS = [
  { key: 'dark', label: 'Dark', bit: 0x01 },
  { key: 'zombie', label: 'Zombie', bit: 0x02 },
  { key: 'poison', label: 'Poison', bit: 0x04 },
  { key: 'vanish', label: 'Vanish', bit: 0x10 },
  { key: 'imp', label: 'Imp', bit: 0x20 },
  { key: 'petrify', label: 'Petrify', bit: 0x40 },
];

/** Character ID -> name, in the standard FF6 roster order this byte uses. */
const CHARACTER_NAMES = [
  'Terra', 'Locke', 'Cyan', 'Shadow', 'Edgar', 'Sabin',
  'Celes', 'Strago', 'Relm', 'Setzer', 'Mog', 'Gau', 'Gogo', 'Umaro',
];

/**
 * The character's own current (possibly player-renamed) display name --
 * exactly the 6 bytes `src/menu/field_menu.asm`'s `DrawCharName` reads
 * (`ldx #6; lda $0002,y`), using the game's text encoding
 * (tools/char_table/text_en.json + fixed_length_en.json in the
 * disassembly): 0x80-0x99 = A-Z, 0x9A-0xB3 = a-z, 0xB4-0xBD = 0-9,
 * 0xFE = space, 0xFF = the fixed-width "pad" filler after a shorter name.
 * Confirmed by reading this exact field during the Narshe intro battle and
 * getting "?????"+pad back -- which is exactly the placeholder name the
 * game itself shows on screen for Terra during that scripted sequence
 * (her real name is a story-mandated mystery at that point), not a
 * decoding bug.
 */
const NAME_LENGTH = 6;
const NAME_PAD_BYTE = 0xff;
const NAME_SPACE_BYTE = 0xfe;

function buildNameByteToChar() {
  const map = new Map();
  for (let i = 0; i < 26; i++) map.set(0x80 + i, String.fromCharCode(65 + i)); // A-Z
  for (let i = 0; i < 26; i++) map.set(0x9a + i, String.fromCharCode(97 + i)); // a-z
  for (let i = 0; i < 10; i++) map.set(0xb4 + i, String.fromCharCode(48 + i)); // 0-9
  map.set(NAME_SPACE_BYTE, ' ');
  return map;
}

const NAME_BYTE_TO_CHAR = buildNameByteToChar();
const NAME_CHAR_TO_BYTE = new Map([...NAME_BYTE_TO_CHAR].map(([byte, char]) => [char, byte]));

function decodeName(wram, offset) {
  let name = '';
  for (let i = 0; i < NAME_LENGTH; i++) {
    const byte = wram[offset + i];
    if (byte === NAME_PAD_BYTE) break; // padding marks the end of the name
    name += NAME_BYTE_TO_CHAR.get(byte) ?? '?';
  }
  return name;
}

/** Encode up to 6 characters (A-Z, a-z, 0-9, space); anything else is dropped. */
function encodeName(wram, offset, text) {
  const chars = [...text].filter((c) => NAME_CHAR_TO_BYTE.has(c)).slice(0, NAME_LENGTH);
  for (let i = 0; i < NAME_LENGTH; i++) {
    wram[offset + i] = i < chars.length ? NAME_CHAR_TO_BYTE.get(chars[i]) : NAME_PAD_BYTE;
  }
}

/**
 * Item ID -> display name, index = item ID (0-255), straight from the
 * game's own item name table (assets/text/item_name.en.txt in the FF6
 * disassembly, which is in ITEM-enum order) with a light cleanup pass on a
 * handful of names the game's fixed-width in-ROM font ran together with no
 * space (e.g. "MITHRILKNIFE"). Item 255 is the game's own "Empty" sentinel
 * -- used here for every equipment slot to mean "nothing equipped".
 */
const ITEM_NAMES = [
  'Dirk', 'Mithril Knife', 'Main Gauche', 'Air Lancet',
  'Thiefknife', 'Assassin', 'Man Eater', 'Swordbreaker',
  'Graedus', 'Valiantknife', 'Mithril Blade', 'Regal Cutlass',
  'Rune Edge', 'Flame Sabre', 'Blizzard', 'Thunderblade',
  'Epee', 'Break Blade', 'Drainer', 'Enhancer',
  'Crystal', 'Falchion', 'Soul Sabre', 'Ogre Nix',
  'Excalibur', 'Scimitar', 'Illumina', 'Ragnarok',
  'Atma Weapon', 'Mithril Pike', 'Trident', 'Stout Spear',
  'Partisan', 'Holy Lance', 'Gold Lance', 'Aura Lance',
  'Imp Halberd', 'Imperial', 'Kodachi', 'Blossom',
  'Hardened', 'Striker', 'Stunner', 'Ashura',
  'Kotetsu', 'Forged', 'Tempest', 'Murasame',
  'Aura', 'Strato', 'Sky Render', 'Heal Rod',
  'Mithril Rod', 'Fire Rod', 'Ice Rod', 'Thunder Rod',
  'Poison Rod', 'Holy Rod', 'Gravity Rod', 'Punisher',
  'Magus Rod', 'Chocobo Brsh', 'Davinci Brsh', 'Magical Brsh',
  'Rainbow Brsh', 'Shuriken', 'Ninja Star', 'Tack Star',
  'Flail', 'Full Moon', 'Morning Star', 'Boomerang',
  'Rising Sun', 'Hawk Eye', 'Bone Club', 'Sniper',
  'Wing Edge', 'Cards', 'Darts', 'Doom Darts',
  'Trump', 'Dice', 'Fixed Dice', 'Metalknuckle',
  'Mithril Claw', 'Kaiser', 'Poison Claw', 'Fire Knuckle',
  'Dragon Claw', 'Tiger Fangs', 'Buckler', 'Heavy Shld',
  'Mithril Shld', 'Gold Shld', 'Aegis Shld', 'Diamond Shld',
  'Flame Shld', 'Ice Shld', 'Thunder Shld', 'Crystal Shld',
  'Genji Shld', 'Tortoise Shld', 'Cursed Shld', 'Paladin Shld',
  'Force Shld', 'Leather Hat', 'Hair Band', 'Plumed Hat',
  'Beret', 'Magus Hat', 'Bandana', 'Iron Helmet',
  'Coronet', 'Bards Hat', 'Green Beret', 'Head Band',
  'Mithril Helm', 'Tiara', 'Gold Helmet', 'Tiger Mask',
  'Red Cap', 'Mystery Veil', 'Circlet', 'Regal Crown',
  'Diamond Helm', 'Dark Hood', 'Crystal Helm', 'Oath Veil',
  'Cat Hood', 'Genji Helmet', 'Thornlet', 'Titanium',
  'Leather Armor', 'Cotton Robe', 'Kung Fu Suit', 'Iron Armor',
  'Silk Robe', 'Mithril Vest', 'Ninja Gear', 'White Dress',
  'Mithril Mail', 'Gaia Gear', 'Mirage Vest', 'Gold Armor',
  'Power Sash', 'Light Robe', 'Diamond Vest', 'Red Jacket',
  'Force Armor', 'Diamond Armor', 'Dark Gear', 'Tao Robe',
  'Crystal Mail', 'Czarina Gown', 'Genji Armor', 'Imps Armor',
  'Minerva', 'Tabby Suit', 'Chocobo Suit', 'Moogle Suit',
  'Nutkin Suit', 'Behemoth Suit', 'Snow Muffler', 'Noiseblaster',
  'Bio Blaster', 'Flash', 'Chain Saw', 'Debilitator',
  'Drill', 'Air Anchor', 'Autocrossbow', 'Fire Skean',
  'Water Edge', 'Bolt Edge', 'Inviz Edge', 'Shadow Edge',
  'Goggles', 'Star Pendant', 'Peace Ring', 'Amulet',
  'White Cape', 'Jewel Ring', 'Fairy Ring', 'Barrier Ring',
  'Mithril Glove', 'Guard Ring', 'Running Shoes', 'Wall Ring',
  'Cherub Down', 'Cure Ring', 'True Knight', 'Dragoonboots',
  'Zephyr Cape', 'Czarina Ring', 'Cursed Ring', 'Earrings',
  'Atlas Armlet', 'Blizzard Orb', 'Rage Ring', 'Sneak Ring',
  'Pod Bracelet', 'Hero Ring', 'Ribbon', 'Muscle Belt',
  'Crystal Orb', 'Gold Hairpin', 'Economizer', 'Thief Glove',
  'Gauntlet', 'Genji Glove', 'Hyper Wrist', 'Offering',
  'Beads', 'Black Belt', 'Coin Toss', 'Fake Mustache',
  'Gem Box', 'Dragon Horn', 'Merit Award', 'Memento Ring',
  'Safety Bit', 'Relic Ring', 'Moogle Charm', 'Charm Bangle',
  'Marvel Shoes', 'Back Guard', 'Gale Hairpin', 'Sniper Sight',
  'Exp Egg', 'Tintinabar', 'Sprint Shoes', 'Rename Card',
  'Tonic', 'Potion', 'X Potion', 'Tincture',
  'Ether', 'X Ether', 'Elixir', 'Megalixir',
  'Fenix Down', 'Revivify', 'Antidote', 'Eyedrop',
  'Soft', 'Remedy', 'Sleeping Bag', 'Tent',
  'Green Cherry', 'Magicite', 'Super Ball', 'Echo Screen',
  'Smoke Bomb', 'Warp Stone', 'Dried Meat', 'Empty',
];

const EMPTY_ITEM = 0xff;

/**
 * Magic spell ID -> display name (0-53), in `MAGIC` enum order, from the
 * game's own spell name table (assets/text/magic_name.en.txt). "Haste" and
 * "Slow" each have a second, distinct spell ID in the enum (an
 * enemy/AI-only casting variant, per the disassembly's own `HASTE2`/`SLOW_2`
 * source labels) that the game itself also displays as plain "Haste"/"Slow"
 * -- labelled "(alt)" here so the two IDs stay distinguishable in a
 * dropdown instead of silently duplicating a name.
 */
const SPELL_NAMES = [
  'Fire', 'Blizzard', 'Thunder', 'Poison', 'Drain', 'Fira', 'Blizzara', 'Thundara',
  'Bio', 'Firaga', 'Blizzaga', 'Thundaga', 'Break', 'Doom', 'Holy', 'Flare',
  'Demi', 'Quarter', 'X-Zone', 'Meteor', 'Ultima', 'Quake', 'Tornado', 'Meltdown',
  'Scan', 'Slow', 'Rasp', 'Mute', 'Safe', 'Sleep', 'Confuse', 'Haste',
  'Stop', 'Berserk', 'Float', 'Imp', 'Reflect', 'Shell', 'Vanish', 'Haste (alt)',
  'Slow (alt)', 'Osmose', 'Warp', 'Quick', 'Dispel', 'Cure', 'Cura', 'Curaga',
  'Raise', 'Arise', 'Poisona', 'Remedy', 'Regen', 'Reraise',
];

/**
 * Per-character "how well is this spell learned" table: base $1A6E, one
 * byte per spell, 54-byte stride PER CHARACTER ID (0-13; the game's
 * hardware-multiplies charId * $36 to find a character's row) -- separate
 * from, and NOT laid out like, the $1600 active-party table above, because
 * spell knowledge is permanent whether or not that character is currently
 * in the field/battle party. 0 = not started, 1-254 = in-progress learn
 * percentage (via an equipped Esper), 0xFF = fully learned/castable.
 * Confirmed against src/menu/skills.asm's `GetMagicLearnPct` (the exact
 * `charId * $36 + spellId` index math via the SNES hardware multiply
 * registers) and its callers' `cmp #$ff` checks for "already known".
 */
const SPELL_TABLE_BASE = 0x1a6e;
const SPELL_STRIDE = 54;
const SPELL_KNOWN = 0xff;

function spellOffset(charId, spellId) {
  return SPELL_TABLE_BASE + charId * SPELL_STRIDE + spellId;
}

/**
 * Strago's known Lores (0-23), a single GLOBAL 24-bit/3-byte mask at $1D29
 * -- not per-character, since only Strago can ever use Lore. Names come
 * from assets/text/lore_desc.en.txt, which (despite the filename) is
 * actually the Lore name list in bit order, not a description. Confirmed
 * against src/menu/skills.asm's `ExpandLoreList` (`lda $1d29,x` / `ror`
 * loop over 3 bytes x 8 bits, LSB first).
 */
const LORE_NAMES = [
  'Condemned', 'Roulette', 'Cleansweep', 'Aqua Rake', 'Aero', 'Blowfish', 'Big Guard', 'Revenge',
  'White Wind', 'L.5 Doom', 'L.4 Flare', 'L.3 Confuse', 'Reflect (Lore)', 'Pearl (Lore)', 'Step Mine', 'Force Field',
  'Discord', 'Sour Mouth', 'Pep Up', 'Rippler', 'Stone', 'Quasar', 'Grand Train', 'Exploder',
];
const LORE_BASE = 0x1d29;

function isLoreKnown(wram, id) {
  return (wram[LORE_BASE + (id >> 3)] & (1 << (id & 7))) !== 0;
}

function setLoreKnown(wram, id, known) {
  const offset = LORE_BASE + (id >> 3);
  const bit = 1 << (id & 7);
  if (known) wram[offset] |= bit;
  else wram[offset] &= ~bit & 0xff;
}

/**
 * Three more learned-skill masks, all the same bit-per-entry shape as Lore
 * and Dance:
 *
 *   Gau's Rages -- 32 bytes at $1D2C, so 256 bits, one per monster index,
 *   LSB first within each byte. Confirmed against `src/menu/skills.asm`'s
 *   rage-list builder (`lda $1d2c,x` then a `ror`-per-bit loop incrementing
 *   the rage index) and `src/battle/battle_end.asm`'s `ora $1d2c,x ; add to
 *   known rages`. Names are the first 256 entries of the game's own
 *   `monster_name.en.txt` (384 total; only the first 256 are rageable).
 *
 *   Sabin's Blitzes -- 8 bits at $1D28, and Cyan's SwdTechs -- 8 bits at
 *   $1CF7. Both confirmed against `GetBlitzList` (`lda $1d28`) sharing its
 *   body with `GetDanceList`, and `init_skills.asm`'s `lda $1cf7 ; known
 *   swdtechs`. Blitz names come from `attack_name.en.txt` starting at the
 *   PUMMEL entry (located by name, not a hardcoded index -- the first
 *   attempt sliced by grep's FILE line numbers, which include the header
 *   lines, and produced monster names instead), SwdTech names from
 *   `bushido_name.en.txt`.
 *
 * Relm has no equivalent: Sketch is a command that draws a random attack
 * from whatever it targets, not a list of learned entries, and there is no
 * "known sketches" mask anywhere in the game.
 */
const RAGE_NAMES = [
  'Guard', 'Soldier', 'Templar', 'Ninja',
  'Samurai', 'Orog', 'Mag Roader 1', 'Retainer',
  'Hazer', 'Dahling', 'Rain Man', 'Brawler',
  'Apokryphos', 'Dark Force', 'Whisper', 'Over Mind',
  'Osteosaur', 'Commander', 'Rhodox', 'Were Rat',
  'Ursus', 'Rhinotaur', 'Steroidite', 'Leafer',
  'Stray Cat', 'Lobo', 'Doberman', 'Vomammoth',
  'Fidor', 'Baskervor', 'Suriander', 'Chimera',
  'Behemoth', 'Mesosaur', 'Pterodon', 'Fossilfang',
  'White Drgn', 'Doom Drgn', 'Brachosaur', 'Tyranosaur',
  'Dark Wind', 'Beakor', 'Vulture', 'Harpy',
  'Hermitcrab', 'Trapper', 'Hornet', 'Crasshoppr',
  'Delta Bug', 'Gilomantis', 'Trilium', 'Nightshade',
  'Tumbleweed', 'Bloompire', 'Trilobiter', 'Siegfried 1',
  'Nautiloid', 'Exocite', 'Anguiform', 'Reach Frog',
  'Lizard', 'Chickenlip', 'Hoover', 'Rider',
  'Chupon Colosseum', 'Pipsqueak', 'M Tekarmor', 'Sky Armor',
  'Telstar', 'Lethal Wpn', 'Vaporite', 'Flan',
  'Ing', 'Humpty', 'Brainpan', 'Cruller',
  'Cactrot', 'Repo Man', 'Harvester', 'Bomb',
  'Still Life', 'Boxed Set', 'Slamdancer', 'Hadesgigas',
  'Pug', 'Magic Urn', 'Mover', 'Figaliz',
  'Buffalax', 'Aspik', 'Ghost', 'Crawler',
  'Sand Ray', 'Areneid', 'Actaneon', 'Sand Horse',
  'Dark Side', 'Mad Oscar', 'Crawly', 'Bleary',
  'Marshal', 'Trooper', 'General', 'Covert',
  'Ogor', 'Warlock', 'Madam', 'Joker',
  'Iron Fist', 'Goblin', 'Apparite', 'Powerdemon',
  'Displayer', 'Vector Pup', 'Peepers', 'Sewer Rat',
  'Slatter', 'Rhinox', 'Rhobite', 'Wild Cat',
  'Red Fang', 'Bounty Man', 'Tusker', 'Ralph',
  'Chitonid', 'Wart Puck', 'Rhyos', 'Srbehemoth Undead',
  'Vectaur', 'Wyvern', 'Zombone', 'Dragon',
  'Brontaur', 'Allosaurus', 'Cirpius', 'Sprinter',
  'Gobbler', 'Harpiai', 'Gloomshell', 'Drop',
  'Mind Candy', 'Weedfeeder', 'Luridan', 'Toe Cutter',
  'Over Grunk', 'Exoray', 'Crusher', 'Uroburos',
  'Primordite', 'Sky Cap', 'Cephaler', 'Maliga',
  'Gigan Toad', 'Geckorex', 'Cluck', 'Land Worm',
  'Test Rider', 'Plutoarmor', 'Tomb Thumb', 'Heavyarmor',
  'Chaser', 'Scullion', 'Poplium', 'Intangir',
  'Misfit', 'Eland', 'Enuo', 'Deep Eye',
  'Greasemonk', 'Neckhunter', 'Grenade', 'Critic',
  'Pan Dora', 'Souldancer', 'Gigantos', 'Mag Roader 2',
  'Spek Tor', 'Parasite', 'Earthguard', 'Coelecite',
  'Anemone', 'Hipocampus', 'Spectre', 'Evil Oscar',
  'Slurm', 'Latimeria', 'Stillgoing', 'Allo Ver',
  'Phase', 'Outsider', 'Barb E', 'Parasoul',
  'Pm Stalker', 'Hemophyte', 'Sp Forces', 'Nohrabbit',
  'Wizard', 'Scrapper', 'Ceritops', 'Commando',
  'Opinicus', 'Poppers', 'Lunaris', 'Garm',
  'Vindr', 'Kiwok', 'Nastidon', 'Rinn',
  'Insecare', 'Vermin', 'Mantodea', 'Bogy',
  'Prussian', 'Black Drgn', 'Adamanchyt', 'Dante',
  'Wirey Drgn', 'Dueller', 'Psychot', 'Muus',
  'Karkass', 'Punisher', 'Balloon', 'Gabbldegak',
  'Gtbehemoth', 'Scorpion', 'Chaos Drgn', 'Spit Fire',
  'Vectagoyle', 'Lich', 'Osprey', 'Mag Roader 3',
  'Bug', 'Sea Flower', 'Fortis', 'Abolisher',
  'Aquila', 'Junk', 'Mandrake', 'First Class',
  'Tap Dancer', 'Necromancr', 'Borras', 'Mag Roader 4',
  'Wild Rat', 'Gold Bear', 'Innoc', 'Trixter',
  'Red Wolf', 'Didalos', 'Woolly', 'Veteran',
  'Sky Base', 'Ironhitman', 'Io', 'Pugs',
];

const BLITZ_NAMES = [
  'Pummel', 'Aurabolt', 'Suplex', 'Fire Dance',
  'Mantra', 'Air Blade', 'Spiraler', 'Bum Rush',
];

const SWDTECH_NAMES = [
  'Dispatch', 'Retort', 'Slash', 'Quadra Slam',
  'Empowerer', 'Stunner', 'Quadra Slice', 'Cleave',
];

/**
 * Mog's Dances: an 8-bit mask at $1D4C, one bit per dance, LSB first -- the
 * same shape as Lore, and sitting immediately after Gau's 32-byte Rage mask
 * at $1D2C. Confirmed against `src/battle/win.asm`'s post-battle learn step
 * (`lda f:BattleBGDance,x` for the battle background's dance index, then
 * `GetBitPtr` / `tsb $1d4c` to set it, branching away if the bit was already
 * set) and the `lda $1d4c ; known dances` reads in `init_skills.asm` and
 * `action_1.asm`. Names and their bit order come from the game's own
 * `assets/text/dance_name.en.txt`, in file order.
 */
const DANCE_NAMES = [
  'Wind Song', 'Forest Suite', 'Desert Aria', 'Love Sonata',
  'Earth Blues', 'Water Rondo', 'Dusk Requiem', 'Snowman Jazz',
];
const DANCE_BASE = 0x1d4c;
const RAGE_BASE = 0x1d2c;
const BLITZ_BASE = 0x1d28;
const SWDTECH_BASE = 0x1cf7;

/** Shared bit-mask accessors for the fixed-size learned-skill masks above. */
function isMaskBitSet(wram, base, id) {
  return (wram[base + (id >> 3)] & (1 << (id & 7))) !== 0;
}

function setMaskBit(wram, base, id, on) {
  const offset = base + (id >> 3);
  const bit = 1 << (id & 7);
  if (on) wram[offset] |= bit;
  else wram[offset] &= ~bit & 0xff;
}

function isDanceKnown(wram, id) {
  return (wram[DANCE_BASE + (id >> 3)] & (1 << (id & 7))) !== 0;
}

function setDanceKnown(wram, id, known) {
  const offset = DANCE_BASE + (id >> 3);
  const bit = 1 << (id & 7);
  if (known) wram[offset] |= bit;
  else wram[offset] &= ~bit & 0xff;
}

/**
 * Owned Espers (found/received, whether or not any character is currently
 * bonded to them) -- a separate GLOBAL 32-bit/4-byte mask at $1A69 (only
 * the low 27 bits are real Espers), immediately before the $1A6E spell
 * table above with no gap, which is itself a nice confirmation the two
 * addresses are right. Same LSB-first bit order as Lore. Confirmed against
 * `src/menu/skills.asm`'s `GetGenjuList` (`lda $1a69,x` / `ror` loop over
 * 4 bytes x 8 bits). Bonding a character to an Esper (see `espers` above)
 * doesn't require owning it first here -- this plugin is a debug tool, not
 * a rules engine -- but this list is what a real "Esper menu" would show.
 */
const OWNED_ESPER_BASE = 0x1a69;

function isEsperOwned(wram, id) {
  return (wram[OWNED_ESPER_BASE + (id >> 3)] & (1 << (id & 7))) !== 0;
}

function setEsperOwned(wram, id, owned) {
  const offset = OWNED_ESPER_BASE + (id >> 3);
  const bit = 1 << (id & 7);
  if (owned) wram[offset] |= bit;
  else wram[offset] &= ~bit & 0xff;
}

/**
 * General inventory: two parallel 256-byte GLOBAL arrays, $1869 (item ID
 * per slot) and $1969 (quantity per slot) -- not indexed by item ID, a
 * slot can hold any item in any position, same as the real game's own
 * "first empty slot" allocation. 0xFF in the item-ID array marks an unused
 * slot. Confirmed against `src/menu/item.asm`'s `CountInventoryItems`
 * (`cpx #$0100` = 256 slots, `cmp #ITEM::EMPTY` for the sentinel) and the
 * many `$1869,y`/`$1969,y` "item"/"item quantity" reads throughout that
 * file.
 */
const INVENTORY_ITEM_BASE = 0x1869;
const INVENTORY_QTY_BASE = 0x1969;
const INVENTORY_SLOTS = 256;
const INVENTORY_MAX_QTY = 99;

/**
 * GP (the game's own spelling for gil): a 3-byte little-endian value at
 * $1860-$1862, immediately before the inventory arrays. Confirmed against
 * `src/field/event.asm`'s "give gil" event command (`lda $1860 / adc $eb /
 * sta $1860 ... adc $1862 / sta $1862` -- a 16-bit add plus a carry into
 * the third byte) and `src/menu/field_menu.asm`'s `ValidateMaxGil`, which
 * clamps the same three bytes against `MAX_GIL`. That cap is 9,999,999 in
 * `const.inc`, so this editor uses the game's own limit rather than the
 * 16,777,215 the three bytes could technically hold -- values above it are
 * clamped back down by the game itself the next time it validates.
 */
const GP_BASE = 0x1860;
const GP_MAX = 9999999;

/**
 * "Rare items" -- the disassembly's own term for FF6's key items (fishing
 * sidequest fish, Setzer's Lola's Letter, Cyan's dream-sequence photos,
 * etc.) -- are a GLOBAL 28-bit flag set spanning $1EBA-$1EBD (LSB first,
 * same as Lore/Esper ownership; only the low nibble of $1EBD is used, per
 * the disassembly's own `and #$0f` mask for the English release).
 * Confirmed against `src/menu/item.asm`'s `GetRareItemList` (`ldx $1eba
 * ; rare item event bits (28 total)`). Only the first 20 bits have a name
 * in the game's own text table (assets/text/rare_item_name.en.txt) -- the
 * remaining 8 are left alone entirely, never read or written, rather than
 * guessed at.
 */
const KEY_ITEM_NAMES = [
  'Cider', 'Clock Key', 'Fish (1)', 'Fish (2)', 'Fish (3)', 'Fish (4)', 'Lump of Metal', "Lola's Letter",
  'Coral', 'Books', 'Royal Letter', 'Rust-Rid', 'Autograph', 'Manicure Set', 'Opera Record', 'Magnifying Glass',
  'Eerie Stone', 'Odd Picture', 'Dull Picture', 'Pendant',
];
const KEY_ITEM_BASE = 0x1eba;

function isKeyItemOwned(wram, id) {
  return (wram[KEY_ITEM_BASE + (id >> 3)] & (1 << (id & 7))) !== 0;
}

function setKeyItemOwned(wram, id, owned) {
  const offset = KEY_ITEM_BASE + (id >> 3);
  const bit = 1 << (id & 7);
  if (owned) wram[offset] |= bit;
  else wram[offset] &= ~bit & 0xff;
}

/**
 * Item stat bonuses (Attack/Defense Power, and the Strength/Speed/Stamina/
 * Magic Power an item grants while equipped) are baked into the CARTRIDGE
 * ROM, not WRAM -- a 30-byte record per item at SNES bus address $D85000
 * (bank $D8, matching the disassembly's own "; d8/5000" ItemProp comment;
 * confirmed empirically in this session by writing a test cheat at that
 * exact address and observing the change land at the file offset an
 * independent value-pattern search had already pinned down for item 0's
 * record). Because it's ROM, not RAM, a live poke through core.rom() would
 * only last until the ROM is next reloaded -- so unlike every other field
 * in this plugin, item bonuses are edited by installing a real, persistent
 * Game Genie/PAR-style cheat (the exact same mechanism the Cheats panel
 * uses), which the cheat engine re-applies every frame from now on. See
 * main.js's wireItemBonusEditor() for where the cheat is actually
 * installed/removed -- this file only computes addresses and codes.
 *
 * Two fields share one byte each (packed as two 4-bit values), so they're
 * edited as a pair sharing one cheat: Strength+Speed at offset 16, Stamina+
 * Magic Power at offset 17. Each nibble is SIGN-MAGNITUDE, not two's
 * complement (bit 3 = sign, bits 0-2 = magnitude 0-7) -- confirmed against
 * src/battle/equip.asm's CalcEquipEffect (`and #$000f; bit #$0008; beq
 * positive; eor #$fff7; inc`), and empirically against a "cursed" item
 * whose four bonuses are all -7 in the source but, decoded the (wrong)
 * two's-complement way, looked like three different values -- decoding
 * sign-magnitude instead made all four correctly read back as -7.
 *
 * Offset 20 ("Power") is Attack Power for weapons/Tools/throwing weapons,
 * but the exact same byte is Defense Power for shields/armor and a damage
 * bonus for a few relics (Gauntlet, Genji Glove, Mithril Glove) -- three
 * different macro names (`attack_power`/`def_power`/`item_power`) all
 * compile to this one byte, hence the deliberately dual label below rather
 * than guessing which meaning applies to a given item.
 *
 * Default values for all three fields (256 items each) were parsed directly
 * from the disassembly's item_prop.asm source and cross-checked byte-for-
 * byte against this exact ROM for every one of the 256 items -- not
 * hand-derived. They're what "no cheat installed" should read as, and what
 * setting a field back to matches means "remove the cheat" rather than
 * "install a no-op one".
 */
const ITEM_PROP_BUS_BASE = 0xd85000;
// core.rom() is indexed from the start of the ROM FILE, not the SNES bus --
// this is that same table's location in the file, found empirically (a
// value-pattern search for several items' known attack_power bytes at a
// consistent stride) and cross-checked against ITEM_PROP_BUS_BASE by
// writing a test cheat at that bus address and confirming it landed here.
const ITEM_PROP_FILE_BASE = 1593344;
const ITEM_PROP_RECORD_SIZE = 30;
const ITEM_PROP_OFFSET = { power: 20, strSpeed: 16, staMag: 17, hitRateOrMagDef: 21, evadeMblock: 26, spellCast: 18, weaponSpecial: 27, relicEffect1: 9, relicEffect2: 10, relicEffect3: 11, relicEffect4: 12, relicEffect5: 13, fieldEffect: 5, equipCharsLo: 1, equipCharsHi: 2, equipStatusHi: 8, equipStatusLo: 25, immuneStatus1: 6, immuneStatus2: 7, elemAttack: 15, elemAbsorb: 22, elemNull: 23, elemWeak: 24 };

/**
 * "Spell cast on hit": a weapon can carry a spell that has a chance to fire
 * for free alongside a normal physical attack (e.g. Flame Sabre -> Fire,
 * Illumina -> Holy) -- one byte, `spellId | flags`, where `spellId` is the
 * exact same 0-53 MAGIC enum `plugin.spells` already covers (reused
 * directly, no separate name list needed) and the top two bits are
 * independent flags from `WEAPON_SPELL_FLAGS` in const.inc.
 *
 * Confirmed against 6 real items' compiled bytes (Flame Sabre/Thunderblade/
 * Break Blade/Soul Sabre/Illumina, plus Dirk as a "no spell" control) before
 * trusting the general parse, then a full 231-item verification against
 * this exact ROM (0 mismatches) before shipping.
 *
 * `spellId` and "no spell at all" share a representation ambiguity worth
 * knowing about: FIRE is spell id 0, and an item with no `spell_cast`
 * directive at all also defaults this whole byte to 0 -- so a raw byte of
 * exactly 0 is treated as "no spell" (`spellCastId: null`) rather than
 * "casts Fire with neither flag set", since every real item in this game
 * that casts Fire does so with at least RAND_CAST set (byte 0x40, not 0).
 * A byte with any flag bit set unambiguously resolves to its spell id even
 * when that id is 0.
 */
const SPELL_CAST_RAND = 0x40;
const SPELL_CAST_USE_AS_ITEM = 0x80;

/**
 * Offset 21 is Hit Rate for weapons/Tools but Magic Defense for shields/
 * armor/relics -- same "one byte, meaning depends on item type" pattern as
 * `power` (`hit_rate`/`mag_def` are literally the same macro variable in
 * item_prop.mac). The one wrinkle `power` doesn't have: for CONSUMABLE-type
 * items this same byte is actually the low byte of a totally different
 * 16-bit status-inflict field (`item_status12`), not a stat at all -- found
 * empirically (a full 231-item verification against this exact ROM turned
 * up exactly one mismatch, id 174) and confirmed against the item_prop.mac
 * source: only Shadow's five throwable "Edge" items (171-175) are
 * CONSUMABLE-typed despite sitting in the middle of the weapon/tool id
 * range, so those five are excluded from this one field.
 */
const EDGE_CONSUMABLE_ITEM_IDS = new Set([171, 172, 173, 174, 175]);

function itemPropAddress(itemId, key) {
  return ITEM_PROP_BUS_BASE + itemId * ITEM_PROP_RECORD_SIZE + ITEM_PROP_OFFSET[key];
}

function decodeSignMagnitude(nibble) {
  const magnitude = nibble & 0x07;
  return nibble & 0x08 ? -magnitude : magnitude;
}

function encodeSignMagnitude(value) {
  const clamped = Math.max(-7, Math.min(7, Math.round(value)));
  return clamped < 0 ? 0x08 | -clamped : clamped;
}

/**
 * Offset 26's evade/mblock nibbles are NOT a plain 0-15 value scaled x10 --
 * that was a real, shipped bug in this plugin (a user's own maxed-out edit
 * to Dirk made their evade/mblock drop to 0 on equip, which is what caught
 * it). The nibble is actually a signed **table index** into
 * `src/battle/equip.asm`'s `EquipEvadeTbl`: `CalcEquipEffect` does
 * `and #$000f; asl; tax; lda f:EquipEvadeTbl,x` -- a plain index, not
 * sign-magnitude and not a raw multiply. The table only has 11 real entries
 * (word-sized, hence the `asl` before indexing), covering nibbles 0-10; a
 * nibble of 11-15 (reachable if this field's number input is clamped too
 * loosely, as it briefly was) indexes straight past the table into whatever
 * ROM bytes follow, an undefined 16-bit "boost" with no correct decoding --
 * which is exactly why a maxed-out (nibble 15) edit read back as garbage in
 * game. This plugin only ever needs to construct/read the 11 legitimate
 * values, so `itemBonusFields`' evade/mblock range is clamped to exactly
 * this table's span (-50 to +50) and `decodeEvadeNibble()` clamps any
 * stray out-of-table nibble (from a cheat installed before this fix) down
 * to the table's last real entry for display, rather than reproducing the
 * game's own undefined read.
 */
const EQUIP_EVADE_TABLE = [0, 10, 20, 30, 40, 50, -10, -20, -30, -40, -50];

function decodeEvadeNibble(nibble) {
  return EQUIP_EVADE_TABLE[Math.min(nibble, EQUIP_EVADE_TABLE.length - 1)];
}

function encodeEvadeNibble(value) {
  const clamped = Math.max(-50, Math.min(50, Math.round(value / 10) * 10));
  return EQUIP_EVADE_TABLE.indexOf(clamped);
}

/**
 * Offset 27's high nibble is "Weapon Special Effect": not a stat at all, but
 * one of a fixed set of hand-written battle behaviours the game dispatches
 * to by this exact nibble value, via jump tables in
 * `src/battle/attacker_effect.asm`/`target_effect.asm` (`array_label
 * ATTACKER_EFFECT/TARGET_EFFECT, ATTACK_SPECIAL_EFFECT::<name>`). The low
 * nibble of the same byte is unrelated (`block_gfx`/`block_type`, which
 * gates counterattack blocking and is left untouched by this field --
 * `buildWeaponSpecialCheat()` reads the CURRENT byte and only replaces the
 * high nibble). `item_prop.mac`'s `weapon_special` macro asserts the value
 * is below 16, so these 16 (including "None") are the only values any real
 * item ever uses -- confirmed by parsing every item's `weapon_special`
 * directive from `item_prop.asm` and cross-checking the resulting nibble
 * against this exact ROM for all 226 non-excluded equippable items (0
 * mismatches; the same 5 Edge items are excluded here as
 * `EDGE_CONSUMABLE_ITEM_IDS`, for the same reason as Hit Rate/Magic
 * Defense -- offset 27 means something else for their CONSUMABLE type too).
 * One-line descriptions below are paraphrased from each effect's own
 * handler, not guessed.
 */
const WEAPON_SPECIAL_NAMES = [
  'None',
  'Thief Knife (attack becomes a steal attempt)',
  'Atma Weapon (ignore Defense, never a critical hit)',
  'Instant Death chance (Striker/Wing Edge/Trump)',
  'Man-Eater (2x damage vs. Human enemies)',
  'Drainer (ignore Defense, drains HP equal to damage dealt)',
  'Soul Sabre (drains HP and MP)',
  'MP Critical (spends 12 MP for a guaranteed critical hit)',
  'Sniper/Hawk Eye (chance of bonus damage, more vs. Floating enemies)',
  'Dice (fixed damage from a dice roll, cannot be dodged)',
  'Valiant Knife (ignore Defense, bonus damage = target\'s missing HP)',
  'Tempest (chance to become a fixed Wind Slash attack)',
  'Heal Rod (heals the target instead of damaging, ignores Defense)',
  'Scimitar/Zantetsuken (instant-death chance, bonus vs. Undead)',
  'Ogre Nix (can break on hit; otherwise MP-cost critical)',
  'MP Critical x2 (spends 28 MP; unused by any vanilla item)',
];

const ITEM_WEAPON_SPECIAL_DEFAULT = [
  0,0,0,0,1,3,4,0,0,10,0,0,7,0,0,0,
  0,0,5,0,0,0,6,14,0,13,7,7,2,0,0,0,
  0,0,0,0,0,0,0,0,0,3,0,0,0,0,11,0,
  0,0,0,12,0,0,0,0,0,0,0,7,0,0,0,0,
  0,0,0,0,0,0,0,0,0,8,0,8,3,0,0,0,
  3,9,9,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

/** 8 hex digits: 6 for the 24-bit address, 2 for the value byte -- confirmed
 * against the vendored core's S9xProActionReplayToRaw (`address = data >> 8;
 * byte = data`). */
function toParCode(address, value) {
  return (
    (address & 0xffffff).toString(16).padStart(6, '0').toUpperCase() +
    (value & 0xff).toString(16).padStart(2, '0').toUpperCase()
  );
}

// prettier-ignore
const ITEM_POWER_DEFAULT = [
  26,30,59,76,88,106,146,164,204,145,38,54,55,108,108,108,
  98,117,121,135,167,176,125,182,217,208,255,255,255,70,93,112,
  150,194,139,227,253,82,93,112,121,190,220,57,66,81,101,110,
  162,199,215,200,60,79,79,79,86,124,120,111,168,60,100,130,
  146,86,132,190,86,95,109,102,117,111,151,172,198,104,115,187,
  133,1,1,55,65,83,95,122,188,215,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,252,0,191,128,125,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,10,
];
// prettier-ignore
const ITEM_STRENGTH_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,2,0,7,7,0,0,0,0,
  0,0,0,3,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  1,0,0,0,0,0,0,0,0,0,0,0,7,0,0,0,
  0,0,0,0,0,0,0,0,2,3,0,0,0,0,0,0,
  0,0,0,0,0,0,-7,0,0,0,0,0,0,0,0,0,
  0,0,0,3,0,0,0,3,4,0,2,1,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  5,0,0,5,0,0,0,0,0,1,5,0,1,2,3,0,
  0,6,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,5,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];
// prettier-ignore
const ITEM_SPEED_DEFAULT = [
  0,0,4,0,3,3,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,2,0,7,3,0,0,0,0,
  0,0,0,2,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,1,
  2,0,0,0,0,0,0,0,0,0,0,0,7,0,0,0,
  0,0,0,0,0,0,0,0,0,2,0,0,0,0,0,0,
  0,0,0,0,0,0,-7,0,0,0,0,0,0,0,0,0,
  2,0,0,1,0,0,0,2,3,1,1,1,0,0,0,0,
  2,0,0,0,0,0,0,-2,0,0,2,0,0,0,6,0,
  1,0,0,2,0,0,6,0,0,2,3,0,2,2,6,0,
  7,6,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,5,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];
// prettier-ignore
const ITEM_STAMINA_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,1,0,7,7,0,0,0,0,
  0,0,0,1,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,
  1,0,0,0,0,0,0,0,0,0,0,0,1,0,0,0,
  0,0,0,0,0,0,0,0,0,2,0,0,0,0,0,0,
  0,0,0,0,0,0,-7,0,0,0,0,0,0,0,0,0,
  0,0,0,2,0,0,0,1,2,0,3,1,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  5,0,0,4,0,0,0,0,0,2,2,0,1,2,2,0,
  0,6,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];
// prettier-ignore
const ITEM_MAGICPOWER_DEFAULT = [
  0,0,0,0,0,2,0,0,0,0,0,0,0,2,2,2,
  0,0,0,7,0,0,0,0,1,0,7,7,0,0,0,0,
  0,3,0,3,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,2,0,0,0,0,0,0,0,7,1,1,1,
  2,0,0,0,0,0,0,0,0,0,0,0,2,0,0,0,
  0,0,0,0,0,0,0,0,1,3,0,0,0,0,0,0,
  0,0,0,0,0,0,-7,0,0,0,0,0,3,5,0,0,
  4,0,0,0,0,2,0,0,0,3,4,1,0,0,0,0,
  4,0,0,0,0,0,0,0,1,0,0,5,0,0,0,0,
  0,2,0,1,0,0,0,5,0,3,3,0,4,2,0,5,
  3,6,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,2,0,0,0,0,0,0,0,0,
  0,0,0,0,0,5,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

// Programmatically extracted from item_prop.asm and verified byte-for-byte
// against this exact ROM (231/231 items, all three arrays -- one true
// mismatch found and explained, not silently ignored: see
// EDGE_CONSUMABLE_ITEM_IDS above).
// prettier-ignore
const ITEM_HIT_RATE_OR_MAGDEF_DEFAULT = [
  180,180,180,180,180,180,180,180,180,180,150,150,150,150,150,150,
  150,150,150,150,150,150,150,150,150,150,255,150,150,150,150,150,
  150,150,150,180,150,180,180,180,180,180,180,150,150,150,150,150,
  150,150,150,255,135,135,135,135,135,135,135,150,135,135,135,135,
  135,230,230,230,150,230,150,230,230,180,150,180,230,230,230,230,
  230,2,3,200,200,200,200,200,200,200,10,14,18,23,52,27,
  28,28,28,34,50,66,0,59,70,7,8,9,21,16,10,12,
  23,21,13,10,13,20,15,13,17,25,19,23,18,17,19,31,
  33,38,0,42,19,21,23,27,29,30,32,35,34,43,36,37,
  35,43,44,55,68,47,46,50,49,64,80,100,70,36,38,52,
  67,73,90,255,255,255,255,255,255,255,255,0,0,0,0,0,
  0,0,0,0,5,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,200,
];
// prettier-ignore
const ITEM_EVADE_DEFAULT = [
  0,0,10,0,10,10,0,30,10,10,0,0,10,0,0,0,
  0,0,10,0,0,10,10,0,20,0,50,30,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,10,
  0,0,20,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,10,10,10,10,20,10,
  20,20,20,10,20,30,0,40,0,0,0,0,0,0,0,0,
  0,0,10,0,0,0,0,0,0,0,0,0,0,0,0,0,
  10,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,10,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  10,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,20,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];
// prettier-ignore
const ITEM_MBLOCK_DEFAULT = [
  0,0,0,0,10,0,10,0,0,0,0,0,0,0,0,0,
  0,0,0,20,0,0,0,0,0,0,50,30,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,30,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,40,0,
  10,10,10,0,20,30,0,40,50,0,0,0,0,0,0,0,
  0,10,0,0,0,0,0,0,0,10,0,0,0,0,0,0,
  10,0,0,0,0,0,0,0,0,0,0,0,0,0,10,0,
  0,0,0,0,30,0,0,10,0,0,0,0,10,0,0,0,
  0,0,10,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,10,0,0,0,0,0,0,0,0,0,0,0,
  10,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

/**
 * Spell cast on hit (byte offset 18): `spellId` (0-53, same MAGIC enum as
 * `plugin.spells`) or -1 for "no spell", plus its two independent flag bits
 * decoded separately below. Parsed programmatically from every item's
 * `spell_cast`/`spell_cast_flags` directive in the disassembly source and
 * confirmed byte-for-byte against this exact ROM across all 231 equippable
 * items (0 mismatches) before shipping -- see the field-offset comment above
 * `ITEM_PROP_OFFSET` for the "byte 0 is ambiguous with Fire+no-flags" note.
 */
const ITEM_SPELL_CAST_ID_DEFAULT = [
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,0,1,2,
  -1,12,-1,-1,-1,-1,13,-1,-1,-1,14,15,-1,-1,-1,-1,
  -1,14,-1,-1,-1,-1,-1,-1,-1,-1,32,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,5,6,7,3,14,17,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,13,
  -1,-1,-1,-1,-1,-1,3,0,-1,-1,-1,-1,-1,-1,-1,-1,
  9,10,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
  -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
];

const ITEM_SPELL_CAST_RANDOM_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,1,1,1,
  0,1,0,0,0,0,1,0,0,0,1,1,0,0,0,0,
  0,1,0,0,0,0,0,0,0,0,1,0,0,0,0,0,
  0,0,0,0,0,1,1,1,1,1,1,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,
  0,0,0,0,0,0,1,1,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const ITEM_SPELL_CAST_USEITEM_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,1,1,1,1,1,1,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  1,1,1,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

/** Highest item ID this plugin exposes for bonus editing -- weapons through
 * relics (231 items); consumables/key items past this have no equip bonus. */
/**
 * Relic effects: five bytes (offsets 9-13) of pure flag bits, each bit a
 * named behaviour the battle/menu code checks for directly -- this is how
 * the famous relics actually work, and it is NOT the same mechanism as the
 * Strength/Vigor stat nibble at offset 16. Hyper Wrist, for example, sets
 * RELIC_EFFECT3::STRENGTH_PLUS_50 (byte 3, bit 7) and `src/battle/equip.asm`
 * responds with `vigor = vigor + vigor/2` -- a x1.5 MULTIPLIER applied after
 * every flat equipment bonus has been summed, which is why no value of the
 * +-7 Strength field can reproduce it (a user hit exactly this and asked).
 *
 * `CalcEquipEffect` ORs these bytes in (`tsb`) for EVERY equipped item, not
 * just relics, so a weapon or a piece of armour carrying one of these bits
 * gets the effect too -- which is what makes exposing them here useful
 * rather than merely descriptive.
 *
 * Unlike offset 27 (whose low nibble is unrelated block_gfx/block_type
 * data), each of these five bytes is wholly owned by relic effects, so each
 * is written as one whole-byte cheat.
 *
 * Flags and per-item defaults were parsed straight from `const.inc`'s
 * RELIC_EFFECT1-5 enums and every item's `relic_effect*` directives, then
 * verified byte-for-byte against this exact ROM: all 1155 bytes (231
 * equippable items x 5) matched with zero mismatches. One trap the
 * verification settled: `const.inc` lists DRAGON_HORN inside RELIC_EFFECT1
 * on the same bit as MP_PLUS_12, but the only item that uses it declares
 * `relic_effect2 DRAGON_HORN` -- so byte 1 bit 7 is MP_PLUS_12 and the
 * RELIC_EFFECT1 entry is a stray alias, dropped here.
 */
/**
 * Three more flag-byte groups, all verified together against this exact ROM
 * (1833 bytes across 231 items, 0 mismatches):
 *
 *   Status granted while equipped -- the 16-bit `equip_status` field, and
 *   the one non-obvious layout here: it is emitted as its HIGH byte at
 *   offset 8 and its LOW byte all the way down at offset 25, so the two
 *   halves are not adjacent. High byte = STATUS3 (Float, Regen, Haste,
 *   Shell, Safe, Reflect, and the nasty ones: Slow, Stop), low byte =
 *   STATUS2 (Condemned, Silence, Berserk, Confuse, Sap, Sleep, Image) --
 *   which is exactly how the Cursed Shield inflicts its pile of statuses.
 *
 *   Status immunity -- `immune_status`, a normal little-endian word at
 *   offsets 6-7: low byte STATUS1 (Blind/Zombie/Poison/Imp/Petrify/Death),
 *   high byte STATUS2. This is the Ribbon mechanism.
 *
 *   Elemental -- offset 15 is dual-meaning in the same way `power` and
 *   `hit_rate` are: the source spells it `elem_attack` on weapons but
 *   `elem_half` on armour, and both macros assign the same variable, so one
 *   byte means "attacks with this element" or "halves this element"
 *   depending on what is wearing it. Offsets 22/23/24 are absorb/nullify/
 *   weakness and, like offsets 21 and 27, are consumable-type-dependent --
 *   hence EDGE_CONSUMABLE_ITEM_IDS on that group.
 */
/**
 * Who can equip an item: a 16-bit word at offsets 1-2. Bits 0-13 are the
 * fourteen playable characters in CHAR_FLAG order (Terra first, Umaro
 * last), bit 14 is IMP and bit 15 is MERIT.
 *
 * The MERIT bit is the half of the Merit Award mechanism that lives on the
 * ITEM, and the pair is easy to get backwards: `RELIC_EFFECT4::EQUIP_MERIT`
 * on a worn relic does NOT mean "this character can equip anything". The
 * equip menu builds a mask of just that character's own bit
 * (`GetCharEquipMask`) and, only if the Merit Award effect is active, ORs
 * in `$8000`; an item is then offered if its equip word shares ANY bit with
 * that mask. So Merit Award unlocks exactly the items that carry this MERIT
 * bit themselves -- 143 of them in this ROM, the Dirk included -- and
 * nothing else.
 *
 * Defaults verified against this exact ROM for all 231 equippable items.
 * That check earned its keep here: `item_prop.asm` contains exactly one
 * `.if LANG_EN` conditional in the whole file, and it wraps Atma Weapon's
 * equip line (the Japanese build gives it MERIT, the English build does
 * not). A parser that ignores the conditional gets item 28 wrong and
 * everything else right.
 */
const EQUIP_CHAR_FLAGS = [
  { key: 'terraEquip', offset: 'equipCharsLo', bit: 0x01, label: 'Terra' },
  { key: 'lockeEquip', offset: 'equipCharsLo', bit: 0x02, label: 'Locke' },
  { key: 'cyanEquip', offset: 'equipCharsLo', bit: 0x04, label: 'Cyan' },
  { key: 'shadowEquip', offset: 'equipCharsLo', bit: 0x08, label: 'Shadow' },
  { key: 'edgarEquip', offset: 'equipCharsLo', bit: 0x10, label: 'Edgar' },
  { key: 'sabinEquip', offset: 'equipCharsLo', bit: 0x20, label: 'Sabin' },
  { key: 'celesEquip', offset: 'equipCharsLo', bit: 0x40, label: 'Celes' },
  { key: 'stragoEquip', offset: 'equipCharsLo', bit: 0x80, label: 'Strago' },
  { key: 'relmEquip', offset: 'equipCharsHi', bit: 0x01, label: 'Relm' },
  { key: 'setzerEquip', offset: 'equipCharsHi', bit: 0x02, label: 'Setzer' },
  { key: 'mogEquip', offset: 'equipCharsHi', bit: 0x04, label: 'Mog' },
  { key: 'gauEquip', offset: 'equipCharsHi', bit: 0x08, label: 'Gau' },
  { key: 'gogoEquip', offset: 'equipCharsHi', bit: 0x10, label: 'Gogo' },
  { key: 'umaroEquip', offset: 'equipCharsHi', bit: 0x20, label: 'Umaro' },
  { key: 'impEquip', offset: 'equipCharsHi', bit: 0x40, label: 'Usable while Imp' },
  { key: 'meritEquip', offset: 'equipCharsHi', bit: 0x80, label: 'Merit Award can unlock this item' },
];

const ITEM_EQUIP_CHARS_LO_DEFAULT = [
  219,219,2,130,10,10,219,138,219,2,83,81,81,83,83,83,
  81,81,83,81,81,83,83,81,83,85,83,83,83,16,16,16,
  16,16,16,16,255,8,8,8,8,8,8,4,4,4,4,4,
  4,4,4,128,128,128,128,128,128,128,128,128,128,0,0,0,
  0,0,0,0,193,2,193,2,2,2,0,2,2,0,0,0,
  0,0,0,32,32,32,32,32,32,32,255,87,255,85,255,85,
  255,255,255,85,255,255,255,255,255,255,65,255,0,193,99,87,
  0,255,255,46,95,65,85,32,255,65,255,48,85,42,81,65,
  0,255,255,255,223,129,42,87,193,255,42,65,87,235,255,85,
  46,128,127,48,87,85,42,128,87,0,95,255,65,128,128,128,
  128,128,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  255,255,255,255,255,255,255,255,255,255,255,255,255,255,255,255,
  255,65,255,255,255,0,0,2,255,255,255,255,255,255,255,2,
  255,255,255,255,255,255,0,0,255,255,255,8,255,255,0,255,
  255,255,255,255,255,255,255,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,255,
];

const ITEM_EQUIP_CHARS_HI_DEFAULT = [
  151,151,128,145,128,128,147,145,151,128,128,128,128,128,128,128,
  128,128,128,128,128,128,128,128,128,128,128,128,0,132,132,132,
  132,132,132,132,223,128,128,128,128,128,128,128,128,128,128,128,
  128,128,128,145,145,145,145,145,145,145,145,145,145,129,129,129,
  129,0,0,0,145,128,145,128,128,128,32,128,128,130,130,130,
  130,130,130,128,128,128,128,128,128,128,159,130,159,134,159,130,
  159,159,159,130,159,223,159,159,159,159,1,159,129,149,137,138,
  129,159,159,140,154,1,132,136,159,1,159,0,130,156,130,1,
  1,159,159,223,159,145,136,130,149,159,154,1,130,159,159,134,
  136,145,158,128,130,130,154,145,130,1,130,223,0,1,1,1,
  1,1,44,0,0,0,0,0,0,0,0,0,0,0,0,0,
  63,63,63,63,63,63,63,63,63,63,63,63,63,63,63,31,
  63,1,63,63,63,32,32,16,63,63,63,63,63,63,31,16,
  63,31,63,63,63,63,18,17,31,31,63,1,63,63,4,63,
  63,63,63,63,63,63,63,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const EQUIP_STATUS_FLAGS = [
  { key: 'flyingequipStatusHi', offset: 'equipStatusHi', bit: 0x01, label: 'Float / Flying' },
  { key: 'regenequipStatusHi', offset: 'equipStatusHi', bit: 0x02, label: 'Regen' },
  { key: 'slowequipStatusHi', offset: 'equipStatusHi', bit: 0x04, label: 'Slow' },
  { key: 'hasteequipStatusHi', offset: 'equipStatusHi', bit: 0x08, label: 'Haste' },
  { key: 'stopequipStatusHi', offset: 'equipStatusHi', bit: 0x10, label: 'Stop' },
  { key: 'shellequipStatusHi', offset: 'equipStatusHi', bit: 0x20, label: 'Shell' },
  { key: 'safeequipStatusHi', offset: 'equipStatusHi', bit: 0x40, label: 'Safe' },
  { key: 'reflectequipStatusHi', offset: 'equipStatusHi', bit: 0x80, label: 'Reflect' },
  { key: 'condemnedequipStatusLo', offset: 'equipStatusLo', bit: 0x01, label: 'Condemned (Doom countdown)' },
  { key: 'nearFatalequipStatusLo', offset: 'equipStatusLo', bit: 0x02, label: 'Near Fatal' },
  { key: 'imageequipStatusLo', offset: 'equipStatusLo', bit: 0x04, label: 'Image (auto-Vanish-style dodge)' },
  { key: 'silenceequipStatusLo', offset: 'equipStatusLo', bit: 0x08, label: 'Silence' },
  { key: 'berserkequipStatusLo', offset: 'equipStatusLo', bit: 0x10, label: 'Berserk' },
  { key: 'confuseequipStatusLo', offset: 'equipStatusLo', bit: 0x20, label: 'Confuse' },
  { key: 'sapequipStatusLo', offset: 'equipStatusLo', bit: 0x40, label: 'Sap' },
  { key: 'sleepequipStatusLo', offset: 'equipStatusLo', bit: 0x80, label: 'Sleep' },
];

const IMMUNE_STATUS_FLAGS = [
  { key: 'blindimmuneStatus1', offset: 'immuneStatus1', bit: 0x01, label: 'Blind (Dark)' },
  { key: 'zombieimmuneStatus1', offset: 'immuneStatus1', bit: 0x02, label: 'Zombie' },
  { key: 'poisonimmuneStatus1', offset: 'immuneStatus1', bit: 0x04, label: 'Poison' },
  { key: 'magitekimmuneStatus1', offset: 'immuneStatus1', bit: 0x08, label: 'Magitek' },
  { key: 'vanishimmuneStatus1', offset: 'immuneStatus1', bit: 0x10, label: 'Vanish' },
  { key: 'impimmuneStatus1', offset: 'immuneStatus1', bit: 0x20, label: 'Imp' },
  { key: 'petrifyimmuneStatus1', offset: 'immuneStatus1', bit: 0x40, label: 'Petrify' },
  { key: 'deadimmuneStatus1', offset: 'immuneStatus1', bit: 0x80, label: 'Death' },
  { key: 'condemnedimmuneStatus2', offset: 'immuneStatus2', bit: 0x01, label: 'Condemned (Doom countdown)' },
  { key: 'nearFatalimmuneStatus2', offset: 'immuneStatus2', bit: 0x02, label: 'Near Fatal' },
  { key: 'imageimmuneStatus2', offset: 'immuneStatus2', bit: 0x04, label: 'Image (auto-Vanish-style dodge)' },
  { key: 'silenceimmuneStatus2', offset: 'immuneStatus2', bit: 0x08, label: 'Silence' },
  { key: 'berserkimmuneStatus2', offset: 'immuneStatus2', bit: 0x10, label: 'Berserk' },
  { key: 'confuseimmuneStatus2', offset: 'immuneStatus2', bit: 0x20, label: 'Confuse' },
  { key: 'sapimmuneStatus2', offset: 'immuneStatus2', bit: 0x40, label: 'Sap' },
  { key: 'sleepimmuneStatus2', offset: 'immuneStatus2', bit: 0x80, label: 'Sleep' },
];

const ELEM_ATTACK_FLAGS = [
  { key: 'fireelemAttack', offset: 'elemAttack', bit: 0x01, label: 'Fire' },
  { key: 'iceelemAttack', offset: 'elemAttack', bit: 0x02, label: 'Ice' },
  { key: 'lightningelemAttack', offset: 'elemAttack', bit: 0x04, label: 'Lightning' },
  { key: 'poisonelemAttack', offset: 'elemAttack', bit: 0x08, label: 'Poison' },
  { key: 'windelemAttack', offset: 'elemAttack', bit: 0x10, label: 'Wind' },
  { key: 'holyelemAttack', offset: 'elemAttack', bit: 0x20, label: 'Holy' },
  { key: 'earthelemAttack', offset: 'elemAttack', bit: 0x40, label: 'Earth' },
  { key: 'waterelemAttack', offset: 'elemAttack', bit: 0x80, label: 'Water' },
];

const ELEM_DEFENSE_FLAGS = [
  { key: 'fireelemAbsorb', offset: 'elemAbsorb', bit: 0x01, label: 'Absorb Fire' },
  { key: 'iceelemAbsorb', offset: 'elemAbsorb', bit: 0x02, label: 'Absorb Ice' },
  { key: 'lightningelemAbsorb', offset: 'elemAbsorb', bit: 0x04, label: 'Absorb Lightning' },
  { key: 'poisonelemAbsorb', offset: 'elemAbsorb', bit: 0x08, label: 'Absorb Poison' },
  { key: 'windelemAbsorb', offset: 'elemAbsorb', bit: 0x10, label: 'Absorb Wind' },
  { key: 'holyelemAbsorb', offset: 'elemAbsorb', bit: 0x20, label: 'Absorb Holy' },
  { key: 'earthelemAbsorb', offset: 'elemAbsorb', bit: 0x40, label: 'Absorb Earth' },
  { key: 'waterelemAbsorb', offset: 'elemAbsorb', bit: 0x80, label: 'Absorb Water' },
  { key: 'fireelemNull', offset: 'elemNull', bit: 0x01, label: 'Nullify Fire' },
  { key: 'iceelemNull', offset: 'elemNull', bit: 0x02, label: 'Nullify Ice' },
  { key: 'lightningelemNull', offset: 'elemNull', bit: 0x04, label: 'Nullify Lightning' },
  { key: 'poisonelemNull', offset: 'elemNull', bit: 0x08, label: 'Nullify Poison' },
  { key: 'windelemNull', offset: 'elemNull', bit: 0x10, label: 'Nullify Wind' },
  { key: 'holyelemNull', offset: 'elemNull', bit: 0x20, label: 'Nullify Holy' },
  { key: 'earthelemNull', offset: 'elemNull', bit: 0x40, label: 'Nullify Earth' },
  { key: 'waterelemNull', offset: 'elemNull', bit: 0x80, label: 'Nullify Water' },
  { key: 'fireelemWeak', offset: 'elemWeak', bit: 0x01, label: 'Weak to Fire' },
  { key: 'iceelemWeak', offset: 'elemWeak', bit: 0x02, label: 'Weak to Ice' },
  { key: 'lightningelemWeak', offset: 'elemWeak', bit: 0x04, label: 'Weak to Lightning' },
  { key: 'poisonelemWeak', offset: 'elemWeak', bit: 0x08, label: 'Weak to Poison' },
  { key: 'windelemWeak', offset: 'elemWeak', bit: 0x10, label: 'Weak to Wind' },
  { key: 'holyelemWeak', offset: 'elemWeak', bit: 0x20, label: 'Weak to Holy' },
  { key: 'earthelemWeak', offset: 'elemWeak', bit: 0x40, label: 'Weak to Earth' },
  { key: 'waterelemWeak', offset: 'elemWeak', bit: 0x80, label: 'Weak to Water' },
];

const ITEM_EQUIP_STATUS_HI_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,32,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,64,8,128,1,2,0,0,
  0,0,0,0,0,0,0,0,96,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  106,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const ITEM_EQUIP_STATUS_LO_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,121,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,64,0,0,0,0,0,0,0,0,0,0,0,4,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,1,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,255,
];

const ITEM_IMMUNE_STATUS1_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  1,4,0,7,32,64,5,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,103,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,194,194,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const ITEM_IMMUNE_STATUS2_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,48,0,8,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,248,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const ITEM_ELEM_ATTACK_DEFAULT = [
  0,0,0,16,0,0,0,0,32,0,0,0,0,1,2,4,
  0,0,0,0,0,0,0,0,32,0,0,0,0,0,128,0,
  0,32,0,0,0,0,0,16,0,0,0,0,0,0,16,0,
  0,0,0,0,0,1,2,4,8,32,64,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,32,8,1,32,0,0,0,0,0,0,0,
  0,0,3,0,0,0,0,0,215,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  119,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,87,0,0,0,0,0,0,0,232,0,0,0,
  0,0,1,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const ITEM_ELEM_ABSORB_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  1,2,4,0,0,128,0,39,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,128,0,0,0,0,0,0,0,0,0,64,0,0,
  0,0,0,0,0,0,0,0,0,0,0,128,0,0,0,0,
  0,0,2,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,2,1,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,255,
];

const ITEM_ELEM_NULL_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  2,1,16,0,0,0,0,216,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,1,0,0,0,0,0,0,0,0,23,8,8,8,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,1,4,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const ITEM_ELEM_WEAK_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  128,16,0,0,0,0,207,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,255,
];

/**
 * Field effects (offset 5): a fifth flag byte, separate from the five
 * relic-effect bytes and conceptually different -- these act on the MAP, not
 * in battle. Only four bits are named, and only four items use them
 * (Moogle Charm, Charm Bangle, Tintinabar, Sprint Shoes).
 *
 * Exposed for the same reason the relic effects are: `CalcEquipEffect`
 * accumulates this byte with `tsb $11df` for EVERY equipped item, and the
 * field movement code reads that same accumulator -- `CheckPlayerMove` in
 * `src/field/player.asm` does `lda $11df / and #$20 / ... lda #$03` to pick
 * walking speed 3 instead of 2. So the Sprint Shoes bit on a sword really
 * does make you walk faster; it is not relic-only.
 *
 * One caveat that belongs with the Sprint Shoes bit specifically: the same
 * routine checks `$1eb8 & $02` first and skips the whole thing when set,
 * which is the game's own "sprint disabled" flag for scenes that need normal
 * walking speed. An item can't override that.
 *
 * Defaults verified byte-for-byte against this exact ROM for all 231
 * equippable items (0 mismatches), same as every other field here.
 */
const FIELD_EFFECT_FLAGS = [
  { key: 'reduceRandBattles', offset: 'fieldEffect', bit: 0x01, label: 'Fewer random battles (Charm Bangle)' },
  { key: 'noRandBattles', offset: 'fieldEffect', bit: 0x02, label: 'No random battles (Moogle Charm)' },
  { key: 'sprintShoes', offset: 'fieldEffect', bit: 0x20, label: 'Walk faster (Sprint Shoes)' },
  { key: 'tintinabar', offset: 'fieldEffect', bit: 0x80, label: 'Heal while walking (Tintinabar)' },
];

const ITEM_FIELD_EFFECT_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,2,1,
  0,0,0,0,0,128,32,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const RELIC_EFFECT_FLAGS = [
  { key: 'atlasArmlet1', offset: 'relicEffect1', bit: 0x01, label: '+50% physical damage (Atlas Armlet)' },
  { key: 'earring1', offset: 'relicEffect1', bit: 0x02, label: '+100% magic damage (Earrings)' },
  { key: 'hpPlus251', offset: 'relicEffect1', bit: 0x04, label: '+25% max HP (Red Cap)' },
  { key: 'hpPlus501', offset: 'relicEffect1', bit: 0x08, label: '+50% max HP (Muscle Belt)' },
  { key: 'hpPlus121', offset: 'relicEffect1', bit: 0x10, label: '+12.5% max HP (Green Beret)' },
  { key: 'mpPlus251', offset: 'relicEffect1', bit: 0x20, label: '+25% max MP (Minerva)' },
  { key: 'mpPlus501', offset: 'relicEffect1', bit: 0x40, label: '+50% max MP (Crystal Orb)' },
  { key: 'mpPlus121', offset: 'relicEffect1', bit: 0x80, label: '+12.5% max MP (Bard\'s Hat)' },
  { key: 'galeHairpin2', offset: 'relicEffect2', bit: 0x01, label: 'Gale Hairpin effect' },
  { key: 'backGuard2', offset: 'relicEffect2', bit: 0x02, label: 'Back Guard effect' },
  { key: 'jump2', offset: 'relicEffect2', bit: 0x04, label: 'Jump command (Dragoon Boots)' },
  { key: 'xMagic2', offset: 'relicEffect2', bit: 0x08, label: 'X-Magic command (Gem Box)' },
  { key: 'control2', offset: 'relicEffect2', bit: 0x10, label: 'Control command (Fake Mustache)' },
  { key: 'gilToss2', offset: 'relicEffect2', bit: 0x20, label: 'GP Rain command (Coin Toss)' },
  { key: 'capture2', offset: 'relicEffect2', bit: 0x40, label: 'Capture command (Thief Glove)' },
  { key: 'dragonHorn2', offset: 'relicEffect2', bit: 0x80, label: 'Dragon Horn effect' },
  { key: 'incStealRate3', offset: 'relicEffect3', bit: 0x01, label: 'Better steal rate (Sneak Ring)' },
  { key: 'singleEarring3', offset: 'relicEffect3', bit: 0x02, label: '+50% magic damage (single Earring)' },
  { key: 'incSketchRate3', offset: 'relicEffect3', bit: 0x04, label: 'Better Sketch rate (Beret)' },
  { key: 'incControlRate3', offset: 'relicEffect3', bit: 0x08, label: 'Better Control rate (Coronet)' },
  { key: 'maxHitRate3', offset: 'relicEffect3', bit: 0x10, label: 'Never misses (Sniper Sight)' },
  { key: 'mpCostHalf3', offset: 'relicEffect3', bit: 0x20, label: 'Half MP cost (Gold Hairpin)' },
  { key: 'mpCost13', offset: 'relicEffect3', bit: 0x40, label: 'All spells cost 1 MP (Economizer)' },
  { key: 'strengthPlus503', offset: 'relicEffect3', bit: 0x80, label: 'x1.5 Vigor (Hyper Wrist)' },
  { key: 'xFight4', offset: 'relicEffect4', bit: 0x01, label: 'X-Fight / multi-attack (Offering)' },
  { key: 'randRetal4', offset: 'relicEffect4', bit: 0x02, label: 'Random counterattack (Black Belt)' },
  { key: 'randEvade4', offset: 'relicEffect4', bit: 0x04, label: 'Random evade boost (Beads)' },
  { key: 'gauntlet4', offset: 'relicEffect4', bit: 0x08, label: 'Two-handed grip (Gauntlet)' },
  { key: 'genjiGlove4', offset: 'relicEffect4', bit: 0x10, label: 'Dual-wield weapons (Genji Glove)' },
  { key: 'equipMerit4', offset: 'relicEffect4', bit: 0x20, label: 'Can equip Merit-Award items (Merit Award)' },
  { key: 'cover4', offset: 'relicEffect4', bit: 0x40, label: 'Cover a weakened ally (True Knight)' },
  { key: 'shellHpLow5', offset: 'relicEffect5', bit: 0x01, label: 'Auto-Shell at low HP (Barrier Ring)' },
  { key: 'safeHpLow5', offset: 'relicEffect5', bit: 0x02, label: 'Auto-Safe at low HP (Mithril Glove)' },
  { key: 'reflectHpLow5', offset: 'relicEffect5', bit: 0x04, label: 'Auto-Reflect at low HP (unused in vanilla)' },
  { key: 'doubleExp5', offset: 'relicEffect5', bit: 0x08, label: 'Double EXP (Exp. Egg)' },
  { key: 'doubleGp5', offset: 'relicEffect5', bit: 0x10, label: 'Double GP (Cat Hood)' },
  { key: 'makeUndead5', offset: 'relicEffect5', bit: 0x80, label: 'Become Undead (Relic Ring)' },
];

const ITEM_RELIC_EFFECT1_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,128,16,0,0,0,0,0,4,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,32,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,2,1,0,0,0,0,3,0,8,64,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const ITEM_RELIC_EFFECT2_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,4,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,64,
  0,0,0,0,0,0,32,16,8,128,0,0,0,0,0,0,
  0,2,1,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const ITEM_RELIC_EFFECT3_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,4,0,0,0,
  8,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,1,0,0,0,0,0,32,64,0,
  0,0,128,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,16,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const ITEM_RELIC_EFFECT4_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,64,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  8,16,0,1,4,2,0,0,0,0,32,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

const ITEM_RELIC_EFFECT5_DEFAULT = [
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  16,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,1,2,0,0,0,0,0,0,0,
  0,3,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,128,0,0,
  0,0,0,0,8,0,0,0,0,0,0,0,0,0,0,0,
  0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,
];

/** ITEM_PROP_OFFSET key -> that flag byte's per-item defaults, so
 * buildFlagByteCheat() and itemBonusDefault() can look one up by name. */
const FLAG_BYTE_DEFAULTS = {
  equipCharsLo: ITEM_EQUIP_CHARS_LO_DEFAULT,
  equipCharsHi: ITEM_EQUIP_CHARS_HI_DEFAULT,
  relicEffect1: ITEM_RELIC_EFFECT1_DEFAULT,
  relicEffect2: ITEM_RELIC_EFFECT2_DEFAULT,
  relicEffect3: ITEM_RELIC_EFFECT3_DEFAULT,
  relicEffect4: ITEM_RELIC_EFFECT4_DEFAULT,
  relicEffect5: ITEM_RELIC_EFFECT5_DEFAULT,
  fieldEffect: ITEM_FIELD_EFFECT_DEFAULT,
  equipStatusHi: ITEM_EQUIP_STATUS_HI_DEFAULT,
  equipStatusLo: ITEM_EQUIP_STATUS_LO_DEFAULT,
  immuneStatus1: ITEM_IMMUNE_STATUS1_DEFAULT,
  immuneStatus2: ITEM_IMMUNE_STATUS2_DEFAULT,
  elemAttack: ITEM_ELEM_ATTACK_DEFAULT,
  elemAbsorb: ITEM_ELEM_ABSORB_DEFAULT,
  elemNull: ITEM_ELEM_NULL_DEFAULT,
  elemWeak: ITEM_ELEM_WEAK_DEFAULT,
};

/** Highest item ID this plugin exposes for bonus editing. */
const ITEM_BONUS_MAX_ID = 230;

/**
 * Build the <select> options for one equipment slot: "Empty" first, then
 * every real item in the given ID range (inclusive), which is exactly one
 * equipment category -- ranges taken from the ITEM enum's own
 * FIRST_SHIELD/FIRST_HELMET/FIRST_ARMOR/FIRST_TOOL/FIRST_RELIC/
 * FIRST_CONSUMABLE boundary markers in src/common/const.inc, so a weapon
 * slot can only ever be set to an actual weapon, never a Potion or a Tent.
 */
function equipOptions(lo, hi) {
  const options = [{ value: EMPTY_ITEM, label: 'Empty' }];
  for (let id = lo; id <= hi; id++) options.push({ value: id, label: ITEM_NAMES[id] });
  return options;
}

const WEAPON_OPTIONS = equipOptions(0, 89);
const SHIELD_OPTIONS = equipOptions(90, 104);
const HELMET_OPTIONS = equipOptions(105, 131);
const ARMOR_OPTIONS = equipOptions(132, 162);
const RELIC_OPTIONS = equipOptions(176, 230); // shared pool for both relic slots

/**
 * Off-hand options when the character has the Genji Glove effect: weapons
 * AND shields, which works out to one contiguous id range because shields
 * start immediately after the last weapon (FIRST_SHIELD = 90).
 */
const OFFHAND_DUAL_WIELD_OPTIONS = equipOptions(0, 104);

/** RELIC_EFFECT4::GENJI_GLOVE -- see the RELIC_EFFECT_FLAGS block comment. */
const GENJI_GLOVE_BIT = 0x10;

/**
 * Whether this character currently has the dual-wield effect, which is what
 * lets a weapon sit in the off-hand ("Shield") slot.
 *
 * Deliberately detected from the EFFECT BIT on whatever is equipped rather
 * than by looking for the Genji Glove item id: `CalcEquipEffect` ORs the
 * relic-effect bytes in from every equipped item, not just relics, and the
 * Item Editor now lets that bit be put on anything -- so keying off the item
 * id would miss a sword the user had granted dual-wield to. Reads through
 * `core.rom()`, so an Item Editor cheat is reflected immediately.
 */
function hasDualWield(core, character) {
  const rom = core.rom();
  for (const key of ['weapon', 'shield', 'helmet', 'armor', 'relic1', 'relic2']) {
    const itemId = character[key];
    if (itemId === undefined || itemId === EMPTY_ITEM || itemId > ITEM_BONUS_MAX_ID) continue;
    const byte = rom[ITEM_PROP_FILE_BASE + itemId * ITEM_PROP_RECORD_SIZE + ITEM_PROP_OFFSET.relicEffect4];
    if (byte & GENJI_GLOVE_BIT) return true;
  }
  return false;
}

/**
 * Esper ID -> display name (0-26), from the game's own name table
 * (assets/text/genju_name.en.txt -- "Genju" is the disassembly's internal
 * name for Esper). Kept close to the in-ROM spelling rather than "corrected"
 * to a fan-preferred spelling, since these are already the exact 8-character
 * strings the game itself displays (Alexander is the one clear exception --
 * ALEXANDR is a truncation of a name with an unambiguous full spelling).
 */
const ESPER_NAMES = [
  'Ramuh', 'Ifrit', 'Shiva', 'Siren', 'Terrato', 'Shoat', 'Maduin', 'Bismark',
  'Stray', 'Palidor', 'Tritoch', 'Odin', 'Raiden', 'Bahamut', 'Alexander', 'Crusader',
  'Ragnarok', 'Kirin', 'Zoneseek', 'Carbunkl', 'Phantom', 'Sraphim', 'Golem', 'Unicorn',
  'Fenrir', 'Starlet', 'Phoenix',
];

/**
 * Which Esper a character is bonded to -- offset $1e, one byte, same $1600
 * record and same "Empty" (0xFF) sentinel as the equipment slots just
 * above it. Confirmed against src/menu/field_menu.asm's
 * `DrawCharGenjuName` (`lda $001e,y ; equipped esper`, `cmp #$ff` for "no
 * esper equipped").
 */
const ESPER_OPTIONS = [{ value: EMPTY_ITEM, label: 'None' }, ...ESPER_NAMES.map((label, value) => ({ value, label }))];

function readU16(wram, offset) {
  return wram[offset] | (wram[offset + 1] << 8);
}

function writeU16(wram, offset, value) {
  wram[offset] = value & 0xff;
  wram[offset + 1] = (value >> 8) & 0xff;
}

function readField(wram, base, key) {
  const offset = base + OFFSET[key];
  return WORD_FIELDS.has(key) ? readU16(wram, offset) : wram[offset];
}

function writeField(wram, base, key, value) {
  const offset = base + OFFSET[key];
  if (WORD_FIELDS.has(key)) writeU16(wram, offset, value);
  else wram[offset] = value & 0xff;
}

/**
 * Field metadata the UI renders generically -- see main.js's
 * renderGameEditor(). A plain field is a number input bounded by
 * `min`/`max`; `type: 'select'` fields (equipment) render a dropdown of
 * `options` instead and pass the chosen option's `value` straight to
 * write() with no min/max clamping needed. `fillFrom: <otherKey>` adds a
 * small "Full" button that copies that OTHER field's current value in and
 * commits it the same way a manual edit would -- used by hp/mp so refilling
 * to max doesn't require reading maxHp/maxMp and retyping it.
 */
export const ff6Editor = {
  id: 'ff6-us',
  name: 'Final Fantasy VI',

  matches(header) {
    return header.title === 'FINAL FANTASY 3';
  },

  fields: [
    { key: 'displayName', label: 'Name', type: 'text', maxLength: NAME_LENGTH },
    { key: 'level', label: 'Level', min: 1, max: 99 },
    { key: 'hp', label: 'HP', min: 0, max: 9999, fillFrom: 'maxHp' },
    { key: 'maxHp', label: 'Max HP', min: 1, max: 9999 },
    { key: 'mp', label: 'MP', min: 0, max: 999, fillFrom: 'maxMp' },
    { key: 'maxMp', label: 'Max MP', min: 0, max: 999 },
    { key: 'vigor', label: 'Vigor', min: 0, max: 99 },
    { key: 'speed', label: 'Speed', min: 0, max: 99 },
    { key: 'stamina', label: 'Stamina', min: 0, max: 99 },
    { key: 'magicPower', label: 'Magic Power', min: 0, max: 99 },
  ],

  /** Equipment slots, rendered as their own table -- see main.js. */
  equipment: [
    { key: 'weapon', label: 'Weapon', options: WEAPON_OPTIONS },
    { key: 'shield', label: 'Shield', options: SHIELD_OPTIONS },
    { key: 'helmet', label: 'Helmet', options: HELMET_OPTIONS },
    { key: 'armor', label: 'Armor', options: ARMOR_OPTIONS },
    { key: 'relic1', label: 'Relic 1', options: RELIC_OPTIONS },
    { key: 'relic2', label: 'Relic 2', options: RELIC_OPTIONS },
  ],

  /**
   * Per-character override for an equipment slot's options; null means "use
   * the static `equipment[].options`". Currently only the off-hand slot
   * needs it: with the Genji Glove effect equipped the game lets a second
   * WEAPON go there, so the dropdown has to be able to offer (and display)
   * one -- without this, a save that already has a dual-wielded weapon shows
   * the wrong entry, since the equipped id isn't in the shield-only list.
   * @param {import('../core.js').Snes9xCore} core
   * @param {object} character one entry from read()
   * @param {string} fieldKey one of equipment[].key
   */
  equipmentOptionsFor(core, character, fieldKey) {
    if (fieldKey !== 'shield') return null;
    return hasDualWield(core, character) ? OFFHAND_DUAL_WIELD_OPTIONS : null;
  },

  /**
   * Which Esper each character is bonded to -- its own table (main.js's
   * generic `renderSelectTable()`, the same one `equipment` uses), kept
   * separate from equipment since a bond isn't gear, it's what teaches
   * spells over time and grants a stat bonus on level-up.
   */
  espers: [{ key: 'esper', label: 'Esper Bond', options: ESPER_OPTIONS }],

  /** Curable ailments, for main.js's chip-list UI -- see clearAilment(). */
  ailments: AILMENTS.map(({ key, label }) => ({ key, label })),

  /** All 54 learnable spells, per-character -- see learnSpell()/forgetSpell(). */
  spells: SPELL_NAMES.map((label, id) => ({ id, label })),

  /** Strago's 24 Lores, global (not per-character) -- see readLore(). */
  lore: LORE_NAMES.map((label, id) => ({ id, label })),

  /**
   * Ready-made cheats for this game, offered one click at a time in the
   * Cheats panel so a user doesn't have to hunt down (or hand-derive) a
   * code. These are ROM patches rather than the usual RAM pokes, which
   * works because snes9x's cheat engine applies to ROM addresses too.
   *
   * Each was derived from the disassembly and confirmed against this exact
   * ROM: `field_code` loads into bank $C0 (see `cfg/ff6-en.cfg`) and this is
   * a HiROM cart, so a `$C0/xxxx` address is simply file offset `0xxxxx`.
   *
   * The two walking speeds are the immediate operands of the two `lda #`
   * instructions in `CheckPlayerMove`'s sprint branch -- note the ADDRESS IS
   * THE OPERAND BYTE, not the opcode ($C0/4E30 and $C0/4E34, with the `a9`
   * opcodes one byte earlier). That value is an index into `ObjMoveMult`
   * (`$01,$02,$04,$08,$10,$20,$10,$08,$04,$02`), so normal walking is index
   * 2 (x4) and Sprint Shoes is index 3 (x8) -- exactly the doubling the
   * shoes feel like. Indices 4 and 5 are real entries the engine already
   * uses for faster objects; 6 and up fold back down, so 5 is the ceiling.
   */
  presetCheats: [
    {
      id: 'always-fast-walk',
      name: 'Always walk fast',
      code: 'C04E3403',
      description: 'Normal walking speed becomes Sprint Shoes speed, with no relic equipped.',
    },
    {
      id: 'always-very-fast-walk',
      name: 'Always walk very fast',
      code: 'C04E3404',
      description: 'Normal walking at 4x, double what Sprint Shoes give.',
    },
    {
      id: 'faster-sprint-shoes',
      name: 'Faster Sprint Shoes',
      code: 'C04E3005',
      description: 'Sprint Shoes go from 2x to 8x normal walking. Needs the shoes equipped.',
    },
    {
      id: 'walk-through-walls',
      name: 'Walk through walls (1 of 2)',
      code: 'C04E4880',
      description: 'Skips the terrain checks in CheckPlayerMove. Add BOTH halves of this one.',
    },
    {
      id: 'walk-through-walls-2',
      name: 'Walk through walls (2 of 2)',
      code: 'C04E4953',
      description: 'Second byte of the branch. Useless on its own.',
    },
  ],

  /** Mog's 8 Dances, for the same chip-row UI as Lore -- see DANCE_BASE. */
  dances: DANCE_NAMES.map((label, id) => ({ id, label })),

  /** Gau's 256 Rages, Sabin's 8 Blitzes, Cyan's 8 SwdTechs -- same chip-row
   * UI again; see the block comment above RAGE_NAMES. */
  rages: RAGE_NAMES.map((label, id) => ({ id, label })),
  blitzes: BLITZ_NAMES.map((label, id) => ({ id, label })),
  swdtechs: SWDTECH_NAMES.map((label, id) => ({ id, label })),

  /** All 27 Espers, for the "owned" chip list -- see readEsperInventory(). */
  esperInventory: ESPER_NAMES.map((label, id) => ({ id, label })),

  /** Every real item (0-254; 255 is the "Empty" sentinel, excluded here) -- see readInventory(). */
  inventoryItems: ITEM_NAMES.slice(0, EMPTY_ITEM).map((label, id) => ({ id, label })),

  /** The 20 named key items ("rare items"), for the "owned" chip list -- see readKeyItems(). */
  keyItems: KEY_ITEM_NAMES.map((label, id) => ({ id, label })),

  /** Every item this plugin can install a stat-bonus cheat for -- weapons
   * through relics, not consumables/key items. See readItemBonus() and the
   * build*Cheat() methods below. */
  itemBonusItems: ITEM_NAMES.slice(0, ITEM_BONUS_MAX_ID + 1).map((label, id) => ({ id, label })),

  /**
   * Field metadata for main.js's item-bonus editor UI. `excludeItemIds`
   * (only `hitRateOrMagDef` uses it) hides that one field entirely for an
   * item where the byte doesn't mean this -- see
   * EDGE_CONSUMABLE_ITEM_IDS above. `step` just sets the number
   * input's arrow-key increment. Evade/Magic Block's -50..50 range is not
   * arbitrary -- it's the exact span of `EquipEvadeTbl`'s 11 real entries;
   * see the block comment above `EQUIP_EVADE_TABLE`.
   */
  itemBonusFields: [
    { key: 'power', label: 'Power (ATK/DEF)', min: 0, max: 255 },
    { key: 'strength', label: 'Strength', min: -7, max: 7 },
    { key: 'speed', label: 'Speed', min: -7, max: 7 },
    { key: 'stamina', label: 'Stamina', min: -7, max: 7 },
    { key: 'magicPower', label: 'Magic Power', min: -7, max: 7 },
    { key: 'hitRateOrMagDef', label: 'Hit Rate / Mag. Def.', min: 0, max: 255, excludeItemIds: EDGE_CONSUMABLE_ITEM_IDS },
    { key: 'evade', label: 'Evade', min: -50, max: 50, step: 10 },
    { key: 'mblock', label: 'Magic Block', min: -50, max: 50, step: 10 },
  ],

  /**
   * The 16 named "Weapon Special Effect" options (index = the nibble value
   * `buildWeaponSpecialCheat()` writes) -- see the block comment above
   * `WEAPON_SPECIAL_NAMES` in ff6.js. Exposed here (rather than as another
   * `itemBonusFields` entry) because main.js renders it as its own select,
   * not a plain clamped number -- same reason Spell Cast on Hit isn't in
   * that array either.
   */
  weaponSpecialOptions: WEAPON_SPECIAL_NAMES.map((label, id) => ({ id, label })),

  /** Same exclusion as `hitRateOrMagDef` above, and for the same reason --
   * offset 27 means something else for Shadow's 5 CONSUMABLE-typed "Edge"
   * items. */
  weaponSpecialExcludedItemIds: EDGE_CONSUMABLE_ITEM_IDS,

  /**
   * Every checkbox-style flag group the Item Editor renders, as one list so
   * main.js has a single generic renderer instead of a bespoke block per
   * group. Each flag names the ITEM_PROP_OFFSET key its byte lives under
   * and which bit it is, so the UI never needs to know the record layout;
   * toggling one recomputes that whole byte from its siblings and installs
   * a single cheat for it.
   *
   * `excludeItemIds` hides a whole group for items where those offsets mean
   * something else -- only the elemental defence group needs it (offsets
   * 22-24 are consumable-type-dependent, same as 21 and 27). The relic and
   * field groups deliberately do NOT: offsets 5 and 9-13 mean the same
   * thing for every item type, which the ROM verification confirmed.
   */
  itemFlagGroups: [
    { id: 'equipChars', label: 'Equippable by', flags: EQUIP_CHAR_FLAGS },
    { id: 'relicEffects', label: 'Relic Effects', flags: RELIC_EFFECT_FLAGS },
    { id: 'fieldEffects', label: 'Field Effects (outside battle)', flags: FIELD_EFFECT_FLAGS },
    { id: 'equipStatus', label: 'Status granted while equipped', flags: EQUIP_STATUS_FLAGS },
    { id: 'immuneStatus', label: 'Status immunity', flags: IMMUNE_STATUS_FLAGS },
    { id: 'elemAttack', label: 'Element (weapon: attacks with / armour: halves)', flags: ELEM_ATTACK_FLAGS },
    {
      id: 'elemDefense',
      label: 'Elemental absorb / nullify / weakness',
      flags: ELEM_DEFENSE_FLAGS,
      excludeItemIds: EDGE_CONSUMABLE_ITEM_IDS,
    },
  ],

  /**
   * @param {import('../core.js').Snes9xCore} core
   * @returns {Array<object>} one entry per party member currently occupying
   *   a slot in the active-party table, each
   *   `{ slot, charId, name, dead, activeAilments, knownSpellIds, ...fields, ...equipment }`
   */
  read(core) {
    const wram = core.wram();
    const characters = [];
    for (let slot = 0; slot < SLOT_COUNT; slot++) {
      const base = TABLE_BASE + slot * RECORD_SIZE;
      const charId = wram[base + OFFSET.charId];
      // IDs at/beyond the named roster are battle-only guests (e.g. the
      // intro's escorting soldiers) or an empty slot, not a real party
      // member -- skip them, this editor is for your own characters.
      if (charId >= CHARACTER_NAMES.length) continue;
      const level = wram[base + OFFSET.level];
      if (level === 0) continue; // unused slot

      const status1 = wram[base + OFFSET.status1];
      const character = {
        slot,
        charId,
        name: CHARACTER_NAMES[charId],
        dead: (status1 & STATUS1_DEAD) !== 0,
        activeAilments: AILMENTS.filter((a) => (status1 & a.bit) !== 0).map((a) => a.key),
        knownSpellIds: SPELL_NAMES
          .map((_, id) => id)
          .filter((id) => wram[spellOffset(charId, id)] === SPELL_KNOWN),
      };
      character.displayName = decodeName(wram, base + OFFSET.name);
      for (const field of this.fields) {
        if (field.key === 'displayName') continue; // read separately above, not a plain numeric field
        character[field.key] = readField(wram, base, field.key);
      }
      for (const slotField of this.equipment) character[slotField.key] = readField(wram, base, slotField.key);
      for (const slotField of this.espers) character[slotField.key] = readField(wram, base, slotField.key);
      characters.push(character);
    }
    return characters;
  },

  /**
   * Write one field (stat, name, equipment slot, or Esper bond) for one
   * party slot directly into live WRAM. Takes effect immediately in the
   * running game; make a save state afterward to keep it.
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} slot
   * @param {string} key one of `fields[].key`, `equipment[].key`, or `espers[].key`
   * @param {number|string} value a string for `displayName`, otherwise
   *   already clamped/validated by the caller (see main.js)
   */
  write(core, slot, key, value) {
    const base = TABLE_BASE + slot * RECORD_SIZE;
    if (key === 'displayName') {
      encodeName(core.wram(), base + OFFSET.name, value);
      return;
    }
    if (!(key in OFFSET)) throw new Error(`ff6 editor: unknown field "${key}"`);
    writeField(core.wram(), base, key, value);
  },

  /**
   * Revive a KO'd party member: clear the DEAD bit (and ZOMBIE, since a
   * revived Zombie is a contradiction the game was never meant to represent
   * and would otherwise still render/behave like death) and restore HP to
   * full if it's currently 0 -- matching what a real Phoenix Down/Life spell
   * leaves the character in, rather than a half-dead state with 0 HP but no
   * death flag.
   *
   * Confirmed directly against the real FF6 disassembly
   * (github.com/everything8215/ff6, src/common/const.inc's `STATUS1` enum
   * and src/menu/field_menu.asm's `andflg STATUS1, {DEAD, PETRIFY, ZOMBIE}`
   * check against this exact `$0014,x` offset), not inferred from HP alone.
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} slot
   */
  revive(core, slot) {
    const wram = core.wram();
    const base = TABLE_BASE + slot * RECORD_SIZE;
    wram[base + OFFSET.status1] &= ~(STATUS1_DEAD | STATUS1_ZOMBIE) & 0xff;
    if (readU16(wram, base + OFFSET.hp) === 0) {
      writeU16(wram, base + OFFSET.hp, readU16(wram, base + OFFSET.maxHp));
    }
  },

  /**
   * Clear one specific ailment bit (one of `ailments[].key`) for one party
   * slot, leaving every other Status 1 bit (including DEAD) untouched.
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} slot
   * @param {string} key
   */
  clearAilment(core, slot, key) {
    const ailment = AILMENTS.find((a) => a.key === key);
    if (!ailment) throw new Error(`ff6 editor: unknown ailment "${key}"`);
    const wram = core.wram();
    wram[TABLE_BASE + slot * RECORD_SIZE + OFFSET.status1] &= ~ailment.bit & 0xff;
  },

  /**
   * Mark a spell fully learned (0xFF) for one character, by character ID
   * (0-13, `character.charId` from read()) -- NOT by party slot, since
   * spell knowledge is stored per permanent character, independent of the
   * active party. Takes effect immediately; the in-battle magic menu reads
   * this same byte every time it's opened.
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} charId
   * @param {number} spellId
   */
  learnSpell(core, charId, spellId) {
    core.wram()[spellOffset(charId, spellId)] = SPELL_KNOWN;
  },

  /** Reset a spell to fully unlearned (0). Same addressing as learnSpell(). */
  forgetSpell(core, charId, spellId) {
    core.wram()[spellOffset(charId, spellId)] = 0;
  },

  /**
   * Strago's Lore is global save data, not tied to any character slot --
   * read it independently of read()'s per-character list.
   * @param {import('../core.js').Snes9xCore} core
   * @returns {{ knownIds: number[] }}
   */
  readLore(core) {
    const wram = core.wram();
    const knownIds = LORE_NAMES.map((_, id) => id).filter((id) => isLoreKnown(wram, id));
    return { knownIds };
  },

  /** @param {import('../core.js').Snes9xCore} core @param {number} id */
  learnLore(core, id) {
    setLoreKnown(core.wram(), id, true);
  },

  /** @param {import('../core.js').Snes9xCore} core @param {number} id */
  forgetLore(core, id) {
    setLoreKnown(core.wram(), id, false);
  },

  /**
   * Mog's Dances -- global save data like Lore, not tied to a party slot.
   * @param {import('../core.js').Snes9xCore} core
   * @returns {{ knownIds: number[] }}
   */
  readDances(core) {
    const wram = core.wram();
    return { knownIds: DANCE_NAMES.map((_, id) => id).filter((id) => isDanceKnown(wram, id)) };
  },

  /** @param {import('../core.js').Snes9xCore} core @param {number} id */
  learnDance(core, id) {
    setDanceKnown(core.wram(), id, true);
  },

  /** @param {import('../core.js').Snes9xCore} core @param {number} id */
  forgetDance(core, id) {
    setDanceKnown(core.wram(), id, false);
  },

  /** @param {import('../core.js').Snes9xCore} core */
  readRages(core) {
    const wram = core.wram();
    return { knownIds: RAGE_NAMES.map((_, id) => id).filter((id) => isMaskBitSet(wram, RAGE_BASE, id)) };
  },
  learnRage(core, id) { setMaskBit(core.wram(), RAGE_BASE, id, true); },
  forgetRage(core, id) { setMaskBit(core.wram(), RAGE_BASE, id, false); },

  /** @param {import('../core.js').Snes9xCore} core */
  readBlitzes(core) {
    const wram = core.wram();
    return { knownIds: BLITZ_NAMES.map((_, id) => id).filter((id) => isMaskBitSet(wram, BLITZ_BASE, id)) };
  },
  learnBlitz(core, id) { setMaskBit(core.wram(), BLITZ_BASE, id, true); },
  forgetBlitz(core, id) { setMaskBit(core.wram(), BLITZ_BASE, id, false); },

  /** @param {import('../core.js').Snes9xCore} core */
  readSwdtechs(core) {
    const wram = core.wram();
    return { knownIds: SWDTECH_NAMES.map((_, id) => id).filter((id) => isMaskBitSet(wram, SWDTECH_BASE, id)) };
  },
  learnSwdtech(core, id) { setMaskBit(core.wram(), SWDTECH_BASE, id, true); },
  forgetSwdtech(core, id) { setMaskBit(core.wram(), SWDTECH_BASE, id, false); },

  /**
   * Which Espers have been found/received, independent of which character
   * (if any) is currently bonded to them -- global save data, read
   * separately from read()'s per-character list, same shape as readLore().
   * @param {import('../core.js').Snes9xCore} core
   * @returns {{ knownIds: number[] }}
   */
  readEsperInventory(core) {
    const wram = core.wram();
    const knownIds = ESPER_NAMES.map((_, id) => id).filter((id) => isEsperOwned(wram, id));
    return { knownIds };
  },

  /** @param {import('../core.js').Snes9xCore} core @param {number} id */
  ownEsper(core, id) {
    setEsperOwned(core.wram(), id, true);
  },

  /** @param {import('../core.js').Snes9xCore} core @param {number} id */
  disownEsper(core, id) {
    setEsperOwned(core.wram(), id, false);
  },

  /**
   * The general item inventory -- global save data (not tied to any
   * character), one entry per occupied slot (0-255).
   * @param {import('../core.js').Snes9xCore} core
   * @returns {Array<{slot: number, itemId: number, name: string, qty: number}>}
   */
  readInventory(core) {
    const wram = core.wram();
    const entries = [];
    for (let slot = 0; slot < INVENTORY_SLOTS; slot++) {
      const itemId = wram[INVENTORY_ITEM_BASE + slot];
      if (itemId === EMPTY_ITEM) continue;
      entries.push({ slot, itemId, name: ITEM_NAMES[itemId], qty: wram[INVENTORY_QTY_BASE + slot] });
    }
    return entries;
  },

  /**
   * Change the quantity already in one occupied slot -- does not move or
   * merge the item, just overwrites its count (clamped to 1-99; use
   * removeInventoryItem() to actually clear the slot instead of setting 0).
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} slot
   * @param {number} qty
   */
  setInventoryQty(core, slot, qty) {
    core.wram()[INVENTORY_QTY_BASE + slot] = Math.min(INVENTORY_MAX_QTY, Math.max(1, qty));
  },

  /** Clear one inventory slot entirely (item ID back to the "Empty" sentinel, quantity to 0). */
  removeInventoryItem(core, slot) {
    const wram = core.wram();
    wram[INVENTORY_ITEM_BASE + slot] = EMPTY_ITEM;
    wram[INVENTORY_QTY_BASE + slot] = 0;
  },

  /**
   * Add an item to the general inventory, matching the real game's own
   * allocation: top up an existing slot already holding this exact item if
   * one exists, otherwise claim the first empty slot. Returns the slot used,
   * or null if the inventory is completely full (all 256 slots occupied by
   * something else) and there's nowhere to put it.
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} itemId
   * @param {number} qty
   * @returns {number|null}
   */
  addInventoryItem(core, itemId, qty) {
    const wram = core.wram();
    const clampedQty = Math.min(INVENTORY_MAX_QTY, Math.max(1, qty));
    let firstEmpty = null;
    for (let slot = 0; slot < INVENTORY_SLOTS; slot++) {
      const slotItem = wram[INVENTORY_ITEM_BASE + slot];
      if (slotItem === itemId) {
        const merged = Math.min(INVENTORY_MAX_QTY, wram[INVENTORY_QTY_BASE + slot] + clampedQty);
        wram[INVENTORY_QTY_BASE + slot] = merged;
        return slot;
      }
      if (slotItem === EMPTY_ITEM && firstEmpty === null) firstEmpty = slot;
    }
    if (firstEmpty === null) return null;
    wram[INVENTORY_ITEM_BASE + firstEmpty] = itemId;
    wram[INVENTORY_QTY_BASE + firstEmpty] = clampedQty;
    return firstEmpty;
  },

  /** Max GP this editor will write -- the game's own MAX_GIL, see GP_BASE. */
  gpMax: GP_MAX,

  /**
   * The party's GP -- global save data, a 3-byte little-endian value.
   * @param {import('../core.js').Snes9xCore} core
   * @returns {number}
   */
  readGp(core) {
    const wram = core.wram();
    return wram[GP_BASE] | (wram[GP_BASE + 1] << 8) | (wram[GP_BASE + 2] << 16);
  },

  /** @param {import('../core.js').Snes9xCore} core @param {number} value */
  writeGp(core, value) {
    const clamped = Math.min(GP_MAX, Math.max(0, Math.round(value) || 0));
    const wram = core.wram();
    wram[GP_BASE] = clamped & 0xff;
    wram[GP_BASE + 1] = (clamped >> 8) & 0xff;
    wram[GP_BASE + 2] = (clamped >> 16) & 0xff;
  },

  /**
   * Which key items ("rare items") have been obtained -- global save data,
   * same shape as readLore()/readEsperInventory().
   * @param {import('../core.js').Snes9xCore} core
   * @returns {{ knownIds: number[] }}
   */
  readKeyItems(core) {
    const wram = core.wram();
    const knownIds = KEY_ITEM_NAMES.map((_, id) => id).filter((id) => isKeyItemOwned(wram, id));
    return { knownIds };
  },

  /** @param {import('../core.js').Snes9xCore} core @param {number} id */
  obtainKeyItem(core, id) {
    setKeyItemOwned(core.wram(), id, true);
  },

  /** @param {import('../core.js').Snes9xCore} core @param {number} id */
  removeKeyItem(core, id) {
    setKeyItemOwned(core.wram(), id, false);
  },

  /**
   * Current EFFECTIVE bonus values for one item -- reads straight from
   * core.rom(), which reflects either the item's original ROM byte or
   * whatever an installed cheat is currently forcing it to (the cheat
   * engine re-writes this same buffer every frame; see the block comment
   * above ITEM_PROP_BUS_BASE).
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} itemId
   */
  readItemBonus(core, itemId) {
    return this._readItemBonusFromRom(core.rom(), itemId);
  },

  /**
   * The deterministic cheat ids `build*Cheat()` use for one item, so a
   * caller (main.js's "share this code" display) can look up whichever of
   * them currently exist in `storage`/`state.cheats` without duplicating
   * the id format string.
   * @param {number} itemId
   * @returns {{power: string, strSpeed: string, staMag: string, hitRateOrMagDef: string, evadeMblock: string, spellCast: string}}
   */
  itemBonusCheatIds(itemId) {
    return {
      power: `ff6-item-power-${itemId}`,
      strSpeed: `ff6-item-strspd-${itemId}`,
      staMag: `ff6-item-stamag-${itemId}`,
      hitRateOrMagDef: `ff6-item-hitmagdef-${itemId}`,
      evadeMblock: `ff6-item-evademblock-${itemId}`,
      spellCast: `ff6-item-spellcast-${itemId}`,
      weaponSpecial: `ff6-item-weaponspecial-${itemId}`,
      // Every flag byte, by the same rule buildFlagByteCheat() uses -- so a
      // group added to itemFlagGroups needs no change here.
      ...Object.fromEntries(
        Object.keys(FLAG_BYTE_DEFAULTS).map((key) => [key, `ff6-item-${key.toLowerCase()}-${itemId}`]),
      ),
    };
  },

  /**
   * The item's ORIGINAL bonus values (before any cheat), from data parsed
   * directly out of the game's own item_prop.asm source and cross-checked
   * byte-for-byte against this exact ROM. Setting a field back to this
   * value is what tells the editor to remove the cheat instead of
   * installing a no-op one.
   * @param {number} itemId
   */
  itemBonusDefault(itemId) {
    return {
      power: ITEM_POWER_DEFAULT[itemId] ?? 0,
      strength: ITEM_STRENGTH_DEFAULT[itemId] ?? 0,
      speed: ITEM_SPEED_DEFAULT[itemId] ?? 0,
      stamina: ITEM_STAMINA_DEFAULT[itemId] ?? 0,
      magicPower: ITEM_MAGICPOWER_DEFAULT[itemId] ?? 0,
      hitRateOrMagDef: ITEM_HIT_RATE_OR_MAGDEF_DEFAULT[itemId] ?? 0,
      evade: ITEM_EVADE_DEFAULT[itemId] ?? 0,
      mblock: ITEM_MBLOCK_DEFAULT[itemId] ?? 0,
      spellCastId: ITEM_SPELL_CAST_ID_DEFAULT[itemId] ?? -1,
      spellCastRandom: !!(ITEM_SPELL_CAST_RANDOM_DEFAULT[itemId] ?? 0),
      spellCastUseAsItem: !!(ITEM_SPELL_CAST_USEITEM_DEFAULT[itemId] ?? 0),
      weaponSpecial: ITEM_WEAPON_SPECIAL_DEFAULT[itemId] ?? 0,
      // Keyed by ITEM_PROP_OFFSET name, matching `itemFlagGroups[].flags[].offset`.
      flagBytes: Object.fromEntries(
        Object.entries(FLAG_BYTE_DEFAULTS).map(([key, arr]) => [key, arr[itemId] ?? 0]),
      ),
    };
  },

  /**
   * Compute the cheat that should exist for an item's Power byte, or null
   * if `value` is already the item's default (meaning: remove any existing
   * cheat instead of installing one that does nothing) -- and, either way,
   * poke the live ROM buffer directly with the resulting byte right now.
   * The cheat is what makes the change survive a reload (re-applied fresh
   * every frame from here on); the immediate poke is just so the UI's next
   * read reflects it instantly rather than waiting for the next frame the
   * cheat engine happens to run on.
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} itemId
   * @param {number} value 0-255
   * @returns {{id: string, code: string|null}}
   */
  buildPowerCheat(core, itemId, value) {
    const id = `ff6-item-power-${itemId}`;
    const byte = value & 0xff;
    core.rom()[ITEM_PROP_FILE_BASE + itemId * ITEM_PROP_RECORD_SIZE + ITEM_PROP_OFFSET.power] = byte;
    const isDefault = value === (ITEM_POWER_DEFAULT[itemId] ?? 0);
    return { id, code: isDefault ? null : toParCode(itemPropAddress(itemId, 'power'), byte) };
  },

  /**
   * Strength and Speed share one byte (offset 16), so they're always
   * edited -- and cheated -- as a pair. Same immediate-poke-plus-cheat
   * shape as buildPowerCheat().
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} itemId
   * @param {number} strength -7 to 7
   * @param {number} speed -7 to 7
   * @returns {{id: string, code: string|null}}
   */
  buildStrengthSpeedCheat(core, itemId, strength, speed) {
    const id = `ff6-item-strspd-${itemId}`;
    const byte = encodeSignMagnitude(strength) | (encodeSignMagnitude(speed) << 4);
    core.rom()[ITEM_PROP_FILE_BASE + itemId * ITEM_PROP_RECORD_SIZE + ITEM_PROP_OFFSET.strSpeed] = byte;
    const defStr = ITEM_STRENGTH_DEFAULT[itemId] ?? 0;
    const defSpd = ITEM_SPEED_DEFAULT[itemId] ?? 0;
    const isDefault = strength === defStr && speed === defSpd;
    return { id, code: isDefault ? null : toParCode(itemPropAddress(itemId, 'strSpeed'), byte) };
  },

  /** Stamina and Magic Power share one byte (offset 17) -- same shape as buildStrengthSpeedCheat(). */
  buildStaminaMagicCheat(core, itemId, stamina, magicPower) {
    const id = `ff6-item-stamag-${itemId}`;
    const byte = encodeSignMagnitude(stamina) | (encodeSignMagnitude(magicPower) << 4);
    core.rom()[ITEM_PROP_FILE_BASE + itemId * ITEM_PROP_RECORD_SIZE + ITEM_PROP_OFFSET.staMag] = byte;
    const defSta = ITEM_STAMINA_DEFAULT[itemId] ?? 0;
    const defMag = ITEM_MAGICPOWER_DEFAULT[itemId] ?? 0;
    const isDefault = stamina === defSta && magicPower === defMag;
    return { id, code: isDefault ? null : toParCode(itemPropAddress(itemId, 'staMag'), byte) };
  },

  _readItemBonusFromRom(rom, itemId) {
    const base = ITEM_PROP_FILE_BASE + itemId * ITEM_PROP_RECORD_SIZE;
    const strSpeedByte = rom[base + ITEM_PROP_OFFSET.strSpeed];
    const staMagByte = rom[base + ITEM_PROP_OFFSET.staMag];
    const evadeMblockByte = rom[base + ITEM_PROP_OFFSET.evadeMblock];
    const spellCastByte = rom[base + ITEM_PROP_OFFSET.spellCast];
    return {
      power: rom[base + ITEM_PROP_OFFSET.power],
      strength: decodeSignMagnitude(strSpeedByte & 0x0f),
      speed: decodeSignMagnitude((strSpeedByte >> 4) & 0x0f),
      stamina: decodeSignMagnitude(staMagByte & 0x0f),
      magicPower: decodeSignMagnitude((staMagByte >> 4) & 0x0f),
      hitRateOrMagDef: rom[base + ITEM_PROP_OFFSET.hitRateOrMagDef],
      evade: decodeEvadeNibble(evadeMblockByte & 0x0f),
      mblock: decodeEvadeNibble((evadeMblockByte >> 4) & 0x0f),
      spellCastId: spellCastByte === 0 ? -1 : spellCastByte & 0x3f,
      spellCastRandom: (spellCastByte & SPELL_CAST_RAND) !== 0,
      spellCastUseAsItem: (spellCastByte & SPELL_CAST_USE_AS_ITEM) !== 0,
      weaponSpecial: (rom[base + ITEM_PROP_OFFSET.weaponSpecial] >> 4) & 0x0f,
      flagBytes: Object.fromEntries(
        Object.keys(FLAG_BYTE_DEFAULTS).map((key) => [key, rom[base + ITEM_PROP_OFFSET[key]]]),
      ),
    };
  },

  /**
   * Compute the cheat for Hit Rate (weapons/Tools)/Magic Defense (shields/
   * armor/relics) -- same shape as buildPowerCheat(), a plain unscaled
   * byte. Not meaningful for the five CONSUMABLE-typed "Edge" items (see
   * EDGE_CONSUMABLE_ITEM_IDS); main.js's UI hides the field for those
   * rather than relying on a caller to remember not to call this.
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} itemId
   * @param {number} value 0-255
   * @returns {{id: string, code: string|null}}
   */
  buildHitRateMagDefCheat(core, itemId, value) {
    const id = `ff6-item-hitmagdef-${itemId}`;
    const byte = value & 0xff;
    core.rom()[ITEM_PROP_FILE_BASE + itemId * ITEM_PROP_RECORD_SIZE + ITEM_PROP_OFFSET.hitRateOrMagDef] = byte;
    const isDefault = value === (ITEM_HIT_RATE_OR_MAGDEF_DEFAULT[itemId] ?? 0);
    return { id, code: isDefault ? null : toParCode(itemPropAddress(itemId, 'hitRateOrMagDef'), byte) };
  },

  /**
   * Evade and Magic Block share one byte (offset 26), same pairing pattern
   * as buildStrengthSpeedCheat() -- but a DIFFERENT nibble encoding from
   * every other field here: each nibble is an INDEX into `EquipEvadeTbl`
   * (see the block comment above `EQUIP_EVADE_TABLE`), not sign-magnitude
   * and not a raw x10 multiply. `encodeEvadeNibble()`/`decodeEvadeNibble()`
   * do the real table lookup both ways.
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} itemId
   * @param {number} evade -50 to 50, multiple of 10
   * @param {number} mblock -50 to 50, multiple of 10
   * @returns {{id: string, code: string|null}}
   */
  buildEvadeMblockCheat(core, itemId, evade, mblock) {
    const id = `ff6-item-evademblock-${itemId}`;
    const evadeNibble = encodeEvadeNibble(evade);
    const mblockNibble = encodeEvadeNibble(mblock);
    const byte = evadeNibble | (mblockNibble << 4);
    core.rom()[ITEM_PROP_FILE_BASE + itemId * ITEM_PROP_RECORD_SIZE + ITEM_PROP_OFFSET.evadeMblock] = byte;
    const defEvade = ITEM_EVADE_DEFAULT[itemId] ?? 0;
    const defMblock = ITEM_MBLOCK_DEFAULT[itemId] ?? 0;
    const isDefault = decodeEvadeNibble(evadeNibble) === defEvade && decodeEvadeNibble(mblockNibble) === defMblock;
    return { id, code: isDefault ? null : toParCode(itemPropAddress(itemId, 'evadeMblock'), byte) };
  },

  /**
   * Compute the cheat for "spell cast on hit" (offset 18): a weapon's chance
   * to fire a spell alongside a normal attack. `spellId` is `-1`/`null` for
   * "no spell" (encodes to byte 0, matching every non-casting item's real
   * data); a real spell id ORs in `SPELL_CAST_RAND`/`SPELL_CAST_USE_AS_ITEM`
   * per the two checkboxes. Same immediate-poke-plus-cheat shape as every
   * other build*Cheat() here.
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} itemId
   * @param {number|null} spellId 0-53, or null/-1 for "no spell"
   * @param {boolean} random
   * @param {boolean} useAsItem
   * @returns {{id: string, code: string|null}}
   */
  buildSpellCastCheat(core, itemId, spellId, random, useAsItem) {
    const id = `ff6-item-spellcast-${itemId}`;
    const hasSpell = spellId !== null && spellId !== undefined && spellId >= 0;
    const byte = hasSpell ? ((spellId & 0x3f) | (random ? SPELL_CAST_RAND : 0) | (useAsItem ? SPELL_CAST_USE_AS_ITEM : 0)) : 0;
    core.rom()[ITEM_PROP_FILE_BASE + itemId * ITEM_PROP_RECORD_SIZE + ITEM_PROP_OFFSET.spellCast] = byte;
    const defId = ITEM_SPELL_CAST_ID_DEFAULT[itemId] ?? -1;
    const defRandom = !!(ITEM_SPELL_CAST_RANDOM_DEFAULT[itemId] ?? 0);
    const defUseAsItem = !!(ITEM_SPELL_CAST_USEITEM_DEFAULT[itemId] ?? 0);
    const effectiveId = hasSpell ? spellId : -1;
    const isDefault = effectiveId === defId
      && (!hasSpell || (!!random === defRandom && !!useAsItem === defUseAsItem));
    return { id, code: isDefault ? null : toParCode(itemPropAddress(itemId, 'spellCast'), byte) };
  },

  /**
   * Compute the cheat for "Weapon Special Effect" (offset 27's HIGH nibble
   * -- see the block comment above `WEAPON_SPECIAL_NAMES`). Unlike every
   * other build*Cheat() here, this one must NOT overwrite the whole byte:
   * the low nibble (`block_gfx`/`block_type`) is unrelated data this field
   * never touches, so it reads whatever byte is currently live in
   * `core.rom()` and only replaces the high nibble, preserving the low one
   * exactly as-is (nothing else in this plugin ever writes to it, so it's
   * always either the item's pristine default or, harmlessly, whatever it
   * already was the last time this same method ran).
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} itemId
   * @param {number} value 0-15, an index into WEAPON_SPECIAL_NAMES
   * @returns {{id: string, code: string|null}}
   */
  buildWeaponSpecialCheat(core, itemId, value) {
    const id = `ff6-item-weaponspecial-${itemId}`;
    const rom = core.rom();
    const address = ITEM_PROP_FILE_BASE + itemId * ITEM_PROP_RECORD_SIZE + ITEM_PROP_OFFSET.weaponSpecial;
    const lowNibble = rom[address] & 0x0f;
    const clamped = Math.min(15, Math.max(0, Math.round(value)));
    const byte = lowNibble | (clamped << 4);
    rom[address] = byte;
    const isDefault = clamped === (ITEM_WEAPON_SPECIAL_DEFAULT[itemId] ?? 0);
    return { id, code: isDefault ? null : toParCode(itemPropAddress(itemId, 'weaponSpecial'), byte) };
  },

  /**
   * Compute the cheat for ONE whole flag byte, named by its
   * ITEM_PROP_OFFSET key (`relicEffect3`, `fieldEffect`, `equipStatusHi`,
   * `elemWeak`, ...). Every bit in each of these bytes belongs to the same
   * flag group, so unlike buildWeaponSpecialCheat() there is no
   * neighbouring data to preserve and the caller just passes the whole
   * recomputed byte.
   * @param {import('../core.js').Snes9xCore} core
   * @param {number} itemId
   * @param {string} offsetKey a key of ITEM_PROP_OFFSET that names a flag byte
   * @param {number} value the full 8-bit flag byte
   * @returns {{id: string, code: string|null}}
   */
  buildFlagByteCheat(core, itemId, offsetKey, value) {
    const offset = ITEM_PROP_OFFSET[offsetKey];
    if (offset === undefined) throw new Error(`ff6 editor: unknown flag byte "${offsetKey}"`);
    const id = `ff6-item-${offsetKey.toLowerCase()}-${itemId}`;
    const byte = value & 0xff;
    core.rom()[ITEM_PROP_FILE_BASE + itemId * ITEM_PROP_RECORD_SIZE + offset] = byte;
    const isDefault = byte === (FLAG_BYTE_DEFAULTS[offsetKey][itemId] ?? 0);
    return { id, code: isDefault ? null : toParCode(itemPropAddress(itemId, offsetKey), byte) };
  },

  /**
   * Force every currently-enabled item-editor cheat's byte into `core.rom()`
   * right now, synchronously -- rather than waiting for the cheat engine's
   * own per-frame re-application (`S9xUpdateCheatsInMemory()`, which only
   * runs during `core_run_frame()`).
   *
   * Without this, re-opening the Item Editor and selecting a previously-
   * edited item can briefly show that item's DEFAULT values instead of
   * whatever a stored cheat set them to -- there's a real gap, right after
   * a ROM (re)load, between "the cheat is registered with the core" and
   * "the core has actually run a frame and overwritten the byte", and
   * `readItemBonus()` reads `core.rom()` directly with no idea a cheat even
   * exists. Confirmed empirically in this session (a fresh page load with a
   * pre-existing cheat showed the item's default value, not the cheat's,
   * immediately after selecting it) before writing this fix -- not a
   * theoretical concern.
   *
   * Deliberately does NOT need to decode which item/field a cheat targets:
   * it just parses the PAR code's own address+value (every code this
   * plugin generates is 8 hex digits, `toParCode()`'s exact inverse) and,
   * if that address falls inside the item_prop table's own span, translates
   * it to a file offset using the same bus-address <-> file-offset
   * relationship already verified for `ITEM_PROP_BUS_BASE`/
   * `ITEM_PROP_FILE_BASE` (both bus and file layout are contiguous across
   * the whole table, confirmed by the earlier 231-item verification, so a
   * single linear offset is valid for every address in range, not just the
   * one this session originally tested).
   * @param {import('../core.js').Snes9xCore} core
   * @param {Array<{code: string, enabled: boolean}>} cheats
   */
  primeCheats(core, cheats) {
    const rom = core.rom();
    const tableEnd = ITEM_PROP_BUS_BASE + 256 * ITEM_PROP_RECORD_SIZE;
    for (const cheat of cheats) {
      if (!cheat.enabled || !/^[0-9a-fA-F]{8}$/.test(cheat.code ?? '')) continue;
      const data = parseInt(cheat.code, 16);
      const address = data >>> 8;
      const value = data & 0xff;
      if (address < ITEM_PROP_BUS_BASE || address >= tableEnd) continue;
      rom[address - ITEM_PROP_BUS_BASE + ITEM_PROP_FILE_BASE] = value;
    }
  },
};
